import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import DeliveryAddressPage    from './DeliveryAddressPage';
import AccessPage             from './AccessPage';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import { PERMISSIONS, PAGE_LABELS, MODULE_PAGES } from '../../utils/rbac';

// ── TWENTY COLOURS, NOT NINE (El's item 7) ───────────────────────────────────
//
// "Please give option for 20 colours in the Admin - Settings - Teams so that there aren't
// any duplicate colours."
//
// Avatar colour comes from the person's team, so two teams sharing a colour makes the
// avatars useless for telling teams apart — and with nine colours and more teams than
// that, a duplicate was unavoidable rather than careless.
//
// Chosen to be distinguishable from each other rather than to be a pretty ramp: no two
// are close in hue, and none is near the greys the interface uses. The picker marks the
// ones already taken so a duplicate has to be chosen on purpose.
const PRESET_COLORS = [
  '#0D1D32', '#A1723D', '#2563EB', '#059669', '#7C3AED',
  '#DC2626', '#0891B2', '#D97706', '#DB2777', '#4D7C0F',
  '#1E40AF', '#B91C1C', '#0F766E', '#6D28D9', '#A16207',
  '#BE185D', '#166534', '#7E22CE', '#C2410C', '#334155',
];

const CATEGORIES = [
  // ── JOB TITLES AND GRADE (El's items 1 and 12) ────────────────────────────
  //
  // "Cannot find Admin - Settings - Job Titles."
  //
  // Because it was not here. The dropdown on Add Person and Edit Person reads
  // config_options for the 'job_title' category, and migration 0265 seeded it from the
  // titles already in use — but the category was never added to this list, so there was
  // no screen to tidy the list from. Grade had exactly the same gap.
  { key: 'annual_leave',             label: 'Annual Leave & Company Holidays' },
  { key: 'bid_platform',             label: 'Bid Platform' },
  { key: 'bid_submission_type',      label: 'Bid Submission Type' },
  { key: 'contractor_onboarding',    label: 'Contractor Onboarding' },
  { key: 'dependency_reason',        label: 'Dependency Reason' },
  { key: 'delivery_address',         label: 'Delivery Addresses' },
  { key: 'document_type',            label: 'Document Type' },
  { key: 'employment_type',          label: 'Employment Type' },
  { key: 'grade',                    label: 'Grade' },
  { key: 'job_title',                label: 'Job Titles' },
  { key: 'milestone_status',         label: 'Milestone Status' },
  { key: 'missed_milestone_reason',  label: 'Missed Milestone Reasons' },
  { key: 'module_access',            label: 'Module Access' },
  { key: 'bid_no_bid_reason',        label: 'No-bid Reason' },
  { key: 'overtime_rate_type',       label: 'Overtime Rate Types' },
  { key: 'priority',                 label: 'Priority' },
  { key: 'product_catalog_settings', label: 'Product Catalog' },
  { key: 'qa_steps',                 label: 'QA Review Steps' },
  { key: 'resource_type',            label: 'Resource Types' },
  { key: 'role_definitions',         label: 'Role Definitions' },
  { key: 'roles_permissions',        label: 'Access' },
  { key: 'security_clearance',       label: 'Security Clearance' },
  { key: 'commercial_type',          label: 'SOW Commercial Type' },
  { key: 'sow_issuance',             label: 'SOW Issuance' },
  { key: 'sow_source',               label: 'SOW Source' },
  { key: 'sow_status',               label: 'SOW Status' },
  { key: 'sow_type',                 label: 'SOW Type' },
  { key: 'standby_level',            label: 'Standby Levels' },
  { key: 'supplier_qa_steps',        label: 'Supplier QA Steps' },
  { key: 'integrations',             label: 'System Automation' },
  { key: 'team',                     label: 'Teams' },
  { key: 'user_role',                label: 'User Role' },
];

// ─── Integrations Manager ─────────────────────────────────────────────────────
// Manages app-level integration settings stored in the app_settings table.
// Currently: the procurement shared mailbox address and the other integration
// settings below.
function IntegrationsManager() {
  const { toast } = useToast();
  const [loading,            setLoading]            = useState(true);
  const [mailbox,            setMailbox]            = useState('');
  const [mailboxName,        setMailboxName]        = useState('');
  // Which folder the accounts sync watches, and where a processed email goes.
  const [watchFolder,        setWatchFolder]        = useState('');
  const [processedFolder,    setProcessedFolder]    = useState('');
  const [mailFolders,        setMailFolders]        = useState(null);
  const [foldersLoading,     setFoldersLoading]     = useState(false);
  const [mailboxSaved,       setMailboxSaved]       = useState(false);
  const [mailboxSaving,      setMailboxSaving]      = useState(false);
  const [adhocMailbox,       setAdhocMailbox]       = useState('');
  const [adhocMailboxName,   setAdhocMailboxName]   = useState('');
  const [adhocMailboxSaved,  setAdhocMailboxSaved]  = useState(false);
  const [adhocMailboxSaving, setAdhocMailboxSaving] = useState(false);
  const [m365StandardSku,    setM365StandardSku]    = useState('');
  const [m365PremiumSku,     setM365PremiumSku]     = useState('');
  const [m365Location,       setM365Location]       = useState('');
  const [m365SkuSaved,       setM365SkuSaved]       = useState(false);
  const [m365SkuSaving,      setM365SkuSaving]      = useState(false);
  const [resourceMailbox,    setResourceMailbox]    = useState('');
  const [resourceMbSaved,    setResourceMbSaved]    = useState(false);
  const [resourceMbSaving,   setResourceMbSaving]   = useState(false);
  // Time reminder emails pause toggle
  const [timeReminderPaused, setTimeReminderPaused] = useState(true);
  const [timeReminderSaving, setTimeReminderSaving] = useState(false);
  const [timeReminderSaved,  setTimeReminderSaved]  = useState(false);
  // SMS test send
  const [smsTestTo,          setSmsTestTo]          = useState('');
  const [smsTesting,         setSmsTesting]         = useState(false);
  const [smsTestResult,      setSmsTestResult]      = useState(null);
  // WhatsApp test send
  const [waTestTo,           setWaTestTo]           = useState('');
  const [waTesting,          setWaTesting]          = useState(false);
  const [waTestResult,       setWaTestResult]       = useState(null);
  // SharePoint CoWork
  const [spSitePath,         setSpSitePath]         = useState('');
  const [spFolder,           setSpFolder]           = useState('');
  const [spSaved,            setSpSaved]            = useState(false);
  const [spSaving,           setSpSaving]           = useState(false);
  const [spCopied,           setSpCopied]           = useState(false);
  const [spBrowseOpen,       setSpBrowseOpen]       = useState(false);
  const [spBrowsePath,       setSpBrowsePath]       = useState('');
  const [spBrowseFolders,    setSpBrowseFolders]    = useState(null);
  const [spBrowseLoading,    setSpBrowseLoading]    = useState(false);
  const [spBrowseError,      setSpBrowseError]      = useState('');
  // Bid Tracker SharePoint (a separate site from the SOW Opportunities site)
  const [btSite,             setBtSite]             = useState('');
  const [btParent,           setBtParent]           = useState('');
  const [btTemplate,         setBtTemplate]         = useState('');
  const [btSaved,            setBtSaved]            = useState(false);
  const [btSaving,           setBtSaving]           = useState(false);
  // Default SOW template
  const [sowTemplates,       setSowTemplates]       = useState([]);
  const [defaultTplId,       setDefaultTplId]       = useState('');
  const [defaultTplSaved,    setDefaultTplSaved]    = useState(false);
  const [defaultTplSaving,   setDefaultTplSaving]   = useState(false);
  // TD SYNNEX
  const [tdsynnexBaseUrl,    setTdsynnexBaseUrl]    = useState('');
  const [tdsynnexUrlSaved,   setTdsynnexUrlSaved]   = useState(false);
  const [tdsynnexUrlSaving,  setTdsynnexUrlSaving]  = useState(false);
  const [tdsynnexStatus,     setTdsynnexStatus]     = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('app-settings/procurement_mailbox').catch(() => null),
      api.get('app-settings/procurement_mailbox_name').catch(() => null),
      api.get('app-settings/procurement_inbox_folder').catch(() => null),
      api.get('app-settings/procurement_processed_folder').catch(() => null),
      api.get('app-settings/adhoc_purchase_mailbox').catch(() => null),
      api.get('app-settings/adhoc_purchase_mailbox_name').catch(() => null),
      api.get('app-settings/m365_license_standard_sku_id').catch(() => null),
      api.get('app-settings/m365_license_premium_sku_id').catch(() => null),
      api.get('app-settings/m365_usage_location').catch(() => null),
      api.get('app-settings/resource_portal_mailbox').catch(() => null),
      api.get('app-settings/sharepoint_site_path').catch(() => null),
      api.get('app-settings/sharepoint_cowork_folder').catch(() => null),
      api.get('app-settings/default_sow_template_id').catch(() => null),
      api.get('sow-templates').catch(() => null),
      api.get('app-settings/tdsynnex_base_url').catch(() => null),
      api.get('tdsynnex/status').catch(() => null),
      api.get('app-settings/time_reminder_emails_paused').catch(() => null),
    ]).then(([mb, mbName, watchFol, procFol, adhocMb, adhocMbName, stdSku, premSku, loc, resMb, spSite, spFol, defTpl, tpls, tdUrl, tdStatus, timeRem]) => {
      setMailbox(mb?.value || '');
      setMailboxName(mbName?.value || '');
      setWatchFolder(watchFol?.value || '');
      setProcessedFolder(procFol?.value || '');
      setAdhocMailbox(adhocMb?.value || '');
      setAdhocMailboxName(adhocMbName?.value || '');
      setM365StandardSku(stdSku?.value || '');
      setM365PremiumSku(premSku?.value || '');
      setM365Location(loc?.value || 'GB');
      setResourceMailbox(resMb?.value || '');
      setSpSitePath(spSite?.value || 'altonvalley.sharepoint.com:/sites/Opportunities');
      setSpFolder(spFol?.value || 'Shared Documents/Claude_CoWork');
      setDefaultTplId(defTpl?.value || '');
      const tplArr = Array.isArray(tpls) ? tpls : (tpls?.results || []);
      setSowTemplates(tplArr.filter(t => t.is_active));
      setTdsynnexBaseUrl(tdUrl?.value || tdStatus?.base_url || 'https://api.tdsynnex.com/eu');
      setTdsynnexStatus(tdStatus);
      setTimeReminderPaused(timeRem?.value !== 'false');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('app-settings/bid_tracker_site_path').catch(() => null),
      api.get('app-settings/bid_tracker_parent_folder').catch(() => null),
      api.get('app-settings/bid_tracker_template_folder').catch(() => null),
    ]).then(([s, p, t]) => { setBtSite(s?.value || ''); setBtParent(p?.value || ''); setBtTemplate(t?.value || ''); }).catch(() => {});
  }, []);

  const handleBidSpSave = async () => {
    setBtSaving(true); setBtSaved(false);
    try {
      await Promise.all([
        api.put('app-settings/bid_tracker_site_path',      { value: btSite.trim() }),
        api.put('app-settings/bid_tracker_parent_folder',  { value: btParent.trim() }),
        api.put('app-settings/bid_tracker_template_folder', { value: btTemplate.trim() }),
      ]);
      setBtSaved(true); setTimeout(() => setBtSaved(false), 3000);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setBtSaving(false);
  };

  const handleM365SkuSave = async () => {
    setM365SkuSaving(true);
    setM365SkuSaved(false);
    try {
      await Promise.all([
        api.put('app-settings/m365_license_standard_sku_id', { value: m365StandardSku.trim() }),
        api.put('app-settings/m365_license_premium_sku_id',  { value: m365PremiumSku.trim() }),
        api.put('app-settings/m365_usage_location',          { value: m365Location.trim() || 'GB' }),
      ]);
      setM365SkuSaved(true);
      setTimeout(() => setM365SkuSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setM365SkuSaving(false);
  };


  const handleMailboxSave = async () => {
    setMailboxSaving(true);
    setMailboxSaved(false);
    try {
      await Promise.all([
        api.put('app-settings/procurement_mailbox',      { value: mailbox.trim() }),
        api.put('app-settings/procurement_mailbox_name', { value: mailboxName.trim() }),
        api.put('app-settings/procurement_inbox_folder',     { value: watchFolder.trim() }),
        api.put('app-settings/procurement_processed_folder', { value: processedFolder.trim() }),
      ]);
      setMailboxSaved(true);
      setTimeout(() => setMailboxSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setMailboxSaving(false);
  };

  const handleAdhocMailboxSave = async () => {
    setAdhocMailboxSaving(true);
    setAdhocMailboxSaved(false);
    try {
      await Promise.all([
        api.put('app-settings/adhoc_purchase_mailbox',      { value: adhocMailbox.trim() }),
        api.put('app-settings/adhoc_purchase_mailbox_name', { value: adhocMailboxName.trim() }),
      ]);
      setAdhocMailboxSaved(true);
      setTimeout(() => setAdhocMailboxSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setAdhocMailboxSaving(false);
  };

  const handleSpSave = async () => {
    setSpSaving(true);
    setSpSaved(false);
    try {
      await Promise.all([
        api.put('app-settings/sharepoint_site_path',      { value: spSitePath.trim() }),
        api.put('app-settings/sharepoint_cowork_folder',  { value: spFolder.trim() }),
      ]);
      setSpSaved(true);
      setTimeout(() => setSpSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setSpSaving(false);
  };

  const spBrowse = async (path) => {
    setSpBrowseLoading(true);
    setSpBrowseError('');
    try {
      const res = await api.get('sharepoint/browse', { path: path || '' });
      setSpBrowsePath(res.path);
      setSpBrowseFolders(res.folders || []);
    } catch (e) {
      setSpBrowseError(e.message || 'Browse failed');
      setSpBrowseFolders([]);
    }
    setSpBrowseLoading(false);
  };

  const openSpBrowse = () => {
    setSpBrowseOpen(true);
    setSpBrowsePath('');
    setSpBrowseFolders(null);
    setSpBrowseError('');
    spBrowse('');
  };

  const handleDefaultTplSave = async () => {
    setDefaultTplSaving(true);
    setDefaultTplSaved(false);
    try {
      await api.put('app-settings/default_sow_template_id', { value: defaultTplId.trim() });
      setDefaultTplSaved(true);
      setTimeout(() => setDefaultTplSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setDefaultTplSaving(false);
  };

  const handleResourceMbSave = async () => {
    setResourceMbSaving(true);
    setResourceMbSaved(false);
    try {
      await api.put('app-settings/resource_portal_mailbox', { value: resourceMailbox.trim() });
      setResourceMbSaved(true);
      setTimeout(() => setResourceMbSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setResourceMbSaving(false);
  };

  const handleTdsynnexUrlSave = async () => {
    setTdsynnexUrlSaving(true);
    setTdsynnexUrlSaved(false);
    try {
      await api.put('app-settings/tdsynnex_base_url', { value: tdsynnexBaseUrl.trim() });
      setTdsynnexUrlSaved(true);
      setTimeout(() => setTdsynnexUrlSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setTdsynnexUrlSaving(false);
  };

  const handleTimeReminderToggle = async (newPaused) => {
    setTimeReminderSaving(true);
    setTimeReminderSaved(false);
    try {
      await api.put('app-settings/time_reminder_emails_paused', { value: newPaused ? 'true' : 'false' });
      setTimeReminderPaused(newPaused);
      setTimeReminderSaved(true);
      setTimeout(() => setTimeReminderSaved(false), 3000);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setTimeReminderSaving(false);
  };


  const handleSmsTest = async () => {
    if (!smsTestTo.trim()) return;
    setSmsTesting(true);
    setSmsTestResult(null);
    try {
      const result = await api.post('notify-test/sms', { to: smsTestTo.trim() });
      setSmsTestResult(result?.sent
        ? { ok: true,  msg: `SMS sent to ${smsTestTo.trim()}.` }
        : { ok: false, msg: result?.error || 'Send failed.' });
    } catch (e) {
      setSmsTestResult({ ok: false, msg: e?.message || 'Request failed.' });
    }
    setSmsTesting(false);
  };

  const handleWaTest = async () => {
    if (!waTestTo.trim()) return;
    setWaTesting(true);
    setWaTestResult(null);
    try {
      const result = await api.post('notify-test/whatsapp', { to: waTestTo.trim() });
      setWaTestResult(result?.sent
        ? { ok: true,  msg: `WhatsApp message sent to ${waTestTo.trim()}.` }
        : { ok: false, msg: result?.error || 'Send failed.' });
    } catch (e) {
      setWaTestResult({ ok: false, msg: e?.message || 'Request failed.' });
    }
    setWaTesting(false);
  };

  if (loading) return <div className="lg:col-span-3 p-4 text-[10px] text-gray-400">Loading…</div>;

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-[11px] font-bold text-navy">System Automation</h3>
        <div className="text-[8px] text-gray-400 mt-0.5">Scheduled jobs, automated emails, and external service connections</div>
      </div>

      {/* ── Scheduled Jobs Overview ─────────────────────────────────── */}
      <div className="p-4 border-b border-gray-200">
        <div className="text-[10px] font-bold text-gray-700 mb-3">Scheduled Jobs</div>
        <div className="space-y-1.5">
          {[
            { time: 'Daily · 2:00am UTC',       label: 'TD SYNNEX price sync',               desc: 'Refreshes supplier product prices for all auto-sync enabled catalog items. Logs price changes to history.', tag: 'Procurement' },
            { time: 'Monday · 4:00am UTC',       label: 'SOW AI reference cache refresh',      desc: 'Rebuilds the AI knowledge base from SharePoint CoWork archive and past issued SOWs in R2 storage.', tag: 'AI' },
            { time: 'Daily · 6:00am UTC',        label: 'CRM tender sync',                     desc: 'Fetches new government tender notices from Find a Tender (FTS) and Contracts Finder filtered by configured keywords.', tag: 'CRM' },
            { time: 'Daily · 6:30am UTC',        label: 'CRM news & RSS sync',                 desc: 'Pulls the latest items from all configured RSS news sources into the Industry News feed.', tag: 'CRM' },
            { time: 'Daily · 7:00am UTC',        label: 'M365 new starter activation',         desc: 'Enables Microsoft 365 accounts for employees whose start date is today or earlier, and marks onboarding record.', tag: 'M365' },
            { time: 'Monday · 7:00am UTC',       label: 'Supplier QA digest email',            desc: 'Emails the QA digest recipients a summary of suppliers with expired, outstanding, or soon-expiring compliance documents.', tag: 'Email' },
            { time: '1st of month · 8:00am UTC', label: 'Standby rota Teams notification',     desc: 'Posts a link to the current month\'s standby rota to the configured Microsoft Teams channel via incoming webhook.', tag: 'Teams' },
            { time: 'Weekdays · 8:00am UTC',     label: 'Milestone health-update reminders',   desc: 'Emails project managers and SOW owners who have overdue milestones or stale health updates (RAG status not updated recently).', tag: 'Email' },
            { time: 'Daily · 6:00pm UTC',        label: 'M365 leaver deactivation',            desc: 'Disables Microsoft 365 accounts for employees whose last working day is today, as recorded in offboarding records.', tag: 'M365' },
            { time: 'Weekdays · 9:00am UTC',     label: 'Daily time-log reminders',            desc: 'Emails active staff who have no time logged for the previous working day, to build the daily-logging habit. Can be paused via the setting below.', tag: 'Email' },
            { time: 'Sunday · 6:00pm UTC',       label: 'Contractor timesheet reminders',      desc: 'Emails active contractors who have not yet submitted or received approval for their current week\'s timesheet.', tag: 'Email' },
          ].map(({ time, label, desc, tag }) => {
            const tagColors = {
              Email:       'bg-blue-50 text-blue-700',
              M365:        'bg-indigo-50 text-indigo-700',
              CRM:         'bg-emerald-50 text-emerald-700',
              AI:          'bg-purple-50 text-purple-700',
              Procurement: 'bg-amber-50 text-amber-700',
              Teams:       'bg-sky-50 text-sky-700',
            };
            return (
              <div key={label} className="flex gap-3 px-3 py-2 rounded border border-gray-100 bg-gray-50 hover:bg-white transition-colors">
                <div className="w-44 flex-shrink-0">
                  <div className="text-[8px] font-mono text-gray-500 leading-tight">{time}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[9px] font-semibold text-gray-800">{label}</span>
                    <span className={`text-[7px] font-bold px-1 py-px rounded ${tagColors[tag] || 'bg-gray-100 text-gray-600'}`}>{tag}</span>
                  </div>
                  <div className="text-[8px] text-gray-500 leading-snug">{desc}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-2 text-[8px] text-gray-400">All jobs run on Cloudflare Workers. Emails are sent via Resend and logged to the sent email audit trail.</div>
      </div>

      <div className="p-4">

        {/* Procurement mailbox */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>Procurement — Shared Mailbox</span>
            <span className="bg-purple-100 text-purple-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            The Email Inbox page syncs mail from this shared M365 mailbox. Enter the mailbox address and a display name for the inbox header.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
            Requires the <strong>Mail.Read</strong> Application permission granted on the Entra ID app registration by an Azure admin. The app will authenticate using its existing client credentials — no extra secret needed.
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Mailbox address</label>
              <input
                type="email"
                value={mailbox}
                onChange={e => { setMailbox(e.target.value); setMailboxSaved(false); }}
                placeholder="accounts@altonvalley.com"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Display name</label>
              <input
                type="text"
                value={mailboxName}
                onChange={e => { setMailboxName(e.target.value); setMailboxSaved(false); }}
                placeholder="Accounts Shared Mailbox"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
            </div>

            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Watch folder</label>
              <input
                type="text"
                value={watchFolder}
                onChange={e => { setWatchFolder(e.target.value); setMailboxSaved(false); }}
                placeholder="Inbox (leave blank), or e.g. Accounts/Invoices"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
              <button
                onClick={async () => {
                  setFoldersLoading(true);
                  const r = await api.get('procurement/mail-folders').catch(e => ({ error: e.message, folders: [] }));
                  setMailFolders(r); setFoldersLoading(false);
                }}
                className="px-2 py-1.5 border border-gray-200 rounded text-[9px] font-semibold text-gray-600 hover:bg-gray-50 whitespace-nowrap"
              >{foldersLoading ? 'Loading…' : 'Browse'}</button>
            </div>

            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Move when done</label>
              <input
                type="text"
                value={processedFolder}
                onChange={e => { setProcessedFolder(e.target.value); setMailboxSaved(false); }}
                placeholder="Blank = a folder per outcome (ATV - Matched, etc.)"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
            </div>

            <p className="text-[9px] text-gray-400 pl-[7.5rem]">
              Paths are relative to the Inbox, so <span className="font-mono">Accounts/Invoices</span> means
              Inbox &rsaquo; Accounts &rsaquo; Invoices. Start with a slash to work from the mailbox root instead.
              The watch folder must already exist; the destination is created if it does not.
            </p>

            {/* What is actually in the mailbox, so the paths above can be
                copied rather than remembered. */}
            {mailFolders && (
              <div className="ml-[7.5rem] border border-gray-200 rounded p-2 bg-gray-50 max-h-44 overflow-y-auto">
                {mailFolders.error && <div className="text-[9px] text-red-600">{mailFolders.error}</div>}
                {!mailFolders.error && !mailFolders.folders?.length && (
                  <div className="text-[9px] text-gray-500">No subfolders under the inbox.</div>
                )}
                {(mailFolders.folders || []).map(f => (
                  <button
                    key={f.path}
                    onClick={() => { setWatchFolder(f.path); setMailboxSaved(false); }}
                    className="block w-full text-left text-[9px] text-gray-700 hover:text-navy py-0.5"
                    style={{ paddingLeft: `${(f.depth - 1) * 12}px` }}
                    title={`Use ${f.path} as the watch folder`}
                  >
                    <span className="font-mono">{f.name}</span>
                    {f.count != null && <span className="text-gray-400"> ({f.count})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleMailboxSave}
              disabled={mailboxSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {mailboxSaving ? 'Saving…' : mailboxSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>

        {/* Ad-hoc purchase capture mailbox */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>Ad-hoc Purchases — Shared Mailbox</span>
            <span className="bg-purple-100 text-purple-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            Staff forward Amazon/eBay/etc order confirmations here instead of raising a formal PO for small ad-hoc buys.
            A recurring job extracts what was bought, and the PMO team reviews each one from the Ad-hoc Purchases
            screen (Procurement) — allocate to a SOW or overhead category, raise a PO, or ignore.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
            Requires the same <strong>Mail.Read</strong> Application permission as the Procurement mailbox above — no extra setup needed if that's already granted.
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Mailbox address</label>
              <input
                type="email"
                value={adhocMailbox}
                onChange={e => { setAdhocMailbox(e.target.value); setAdhocMailboxSaved(false); }}
                placeholder="purchases@altonvalley.com"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Display name</label>
              <input
                type="text"
                value={adhocMailboxName}
                onChange={e => { setAdhocMailboxName(e.target.value); setAdhocMailboxSaved(false); }}
                placeholder="Ad-hoc Purchases Mailbox"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleAdhocMailboxSave}
              disabled={adhocMailboxSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {adhocMailboxSaving ? 'Saving…' : adhocMailboxSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>

        {/* Resource Portal — Notifications mailbox */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>Resource Portal — Notifications Mailbox</span>
            <span className="bg-emerald-100 text-emerald-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            Welcome emails to new resource workers (contractors) are sent from this M365 mailbox. Use a shared mailbox
            such as <strong>resources@altonvalley.com</strong> or <strong>noreply@altonvalley.com</strong> — kept separate from the HR mailbox used for permanent staff.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
            Requires the <strong>Mail.Send</strong> Application permission on the Entra ID app registration. The mailbox must exist in your M365 tenant — create it as a shared mailbox in the Microsoft 365 admin centre.
          </div>
          <div className="flex gap-2 items-center mb-2">
            <label className="text-[9px] text-gray-500 w-28 shrink-0">Mailbox address</label>
            <input
              type="email"
              value={resourceMailbox}
              onChange={e => { setResourceMailbox(e.target.value); setResourceMbSaved(false); }}
              placeholder="resources@altonvalley.com"
              className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleResourceMbSave}
              disabled={resourceMbSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {resourceMbSaving ? 'Saving…' : resourceMbSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>

        {/* Messaging — Twilio SMS & WhatsApp */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>Messaging — Twilio SMS &amp; WhatsApp</span>
            <span className="bg-red-100 text-red-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Twilio</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            SMS is sent via <strong>The SMS Works</strong>. WhatsApp alerts use Twilio. Credentials are Worker secrets — not stored here.
            Use the test buttons below to verify your configuration.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2 font-mono space-y-0.5">
            <div className="font-sans font-semibold text-gray-500 not-italic mb-1">SMS (The SMS Works)</div>
            <div>wrangler secret put SMSWORKS_API_KEY &nbsp;&nbsp;&nbsp;<span className="font-sans text-gray-400"># JWT token from your SMS Works dashboard</span></div>
            <div className="font-sans font-semibold text-gray-500 not-italic mt-2 mb-1">WhatsApp (Twilio)</div>
            <div>wrangler secret put TWILIO_ACCOUNT_SID</div>
            <div>wrangler secret put TWILIO_AUTH_TOKEN</div>
            <div>wrangler secret put TWILIO_WHATSAPP_FROM &nbsp;<span className="font-sans text-gray-400"># WhatsApp-enabled number, e.g. +14155238886</span></div>
          </div>

          {/* SMS test */}
          <div className="mb-4">
            <div className="text-[9px] font-semibold text-gray-600 mb-1.5">Send test SMS</div>
            <div className="flex gap-2 items-stretch">
              <input
                type="tel"
                value={smsTestTo}
                onChange={e => { setSmsTestTo(e.target.value); setSmsTestResult(null); }}
                placeholder="+447700900000"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
              <button
                onClick={handleSmsTest}
                disabled={smsTesting || !smsTestTo.trim()}
                className="px-3 py-1.5 border border-gray-200 bg-white text-[10px] font-semibold rounded hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
              >
                {smsTesting ? 'Sending…' : 'Send Test SMS'}
              </button>
            </div>
            {smsTestResult && (
              <div className={`mt-1.5 text-[9px] rounded px-2.5 py-1.5 ${smsTestResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {smsTestResult.ok ? '✓ ' : '✗ '}{smsTestResult.msg}
              </div>
            )}
          </div>

          {/* WhatsApp test */}
          <div>
            <div className="text-[9px] font-semibold text-gray-600 mb-1.5">Send test WhatsApp message</div>
            <div className="text-[8px] text-gray-400 mb-1.5">
              Recipient must have WhatsApp. For the Twilio sandbox, they must first send "join &lt;sandbox-keyword&gt;" to the sandbox number.
            </div>
            <div className="flex gap-2 items-stretch">
              <input
                type="tel"
                value={waTestTo}
                onChange={e => { setWaTestTo(e.target.value); setWaTestResult(null); }}
                placeholder="+447700900000"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
              <button
                onClick={handleWaTest}
                disabled={waTesting || !waTestTo.trim()}
                className="px-3 py-1.5 border border-gray-200 bg-white text-[10px] font-semibold rounded hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
              >
                {waTesting ? 'Sending…' : 'Send Test WhatsApp'}
              </button>
            </div>
            {waTestResult && (
              <div className={`mt-1.5 text-[9px] rounded px-2.5 py-1.5 ${waTestResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {waTestResult.ok ? '✓ ' : '✗ '}{waTestResult.msg}
              </div>
            )}
          </div>
        </div>

        {/* M365 Provisioning — Graph API license SKUs */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>M365 Provisioning — License SKU IDs</span>
            <span className="bg-indigo-100 text-indigo-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            When the <strong>New Starter Form</strong> step is marked complete, OPS automatically creates the M365
            account via Graph API and assigns the license selected at onboarding creation.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
            To find your SKU IDs: go to <strong>Entra admin centre → Billing → Licenses</strong>, or run
            <code className="ml-1 px-1 bg-gray-200 rounded">GET https://graph.microsoft.com/v1.0/subscribedSkus</code> in Graph Explorer
            and copy the <code className="px-1 bg-gray-200 rounded">skuId</code> for each plan.
          </div>
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-36 shrink-0">Business Standard SKU ID</label>
              <input
                type="text"
                value={m365StandardSku}
                onChange={e => { setM365StandardSku(e.target.value); setM365SkuSaved(false); }}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-36 shrink-0">Business Premium SKU ID</label>
              <input
                type="text"
                value={m365PremiumSku}
                onChange={e => { setM365PremiumSku(e.target.value); setM365SkuSaved(false); }}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-36 shrink-0">Usage Location</label>
              <input
                type="text"
                value={m365Location}
                onChange={e => { setM365Location(e.target.value); setM365SkuSaved(false); }}
                placeholder="GB"
                maxLength={2}
                className="w-16 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy uppercase"
              />
              <span className="text-[8px] text-gray-400">ISO 3166-1 alpha-2 country code (e.g. GB, US)</span>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleM365SkuSave}
              disabled={m365SkuSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {m365SkuSaving ? 'Saving…' : m365SkuSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
          {(!m365StandardSku || !m365PremiumSku) && (
            <div className="mt-1.5 text-[8px] text-amber-600">
              {!m365StandardSku && !m365PremiumSku
                ? 'No SKU IDs configured — user will be created but no license will be assigned until SKUs are saved.'
                : `${!m365StandardSku ? 'Business Standard' : 'Business Premium'} SKU ID not set.`}
            </div>
          )}
        </div>

        {/* SharePoint CoWork — SOW reference docs */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>SharePoint — SOW Reference Documents (CoWork)</span>
            <span className="bg-blue-100 text-blue-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            When generating or planning a SOW, OPS pulls up to 3 <code className="bg-gray-100 px-1 rounded">.docx</code> files
            from this SharePoint folder and injects them as reference examples. Place your 3 best, most representative SOWs there
            — quality matters more than quantity.
          </div>
          <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
            Site path uses the Graph API format: <code className="bg-gray-200 px-1 rounded">tenant.sharepoint.com:/sites/SiteName</code>.
            Folder path is relative to the document library root, e.g. <code className="bg-gray-200 px-1 rounded">Shared Documents/Claude_CoWork</code>.
            Uses the same Entra app registration credentials as sign-in (ENTRA_TENANT_ID / ENTRA_CLIENT_ID / ENTRA_CLIENT_SECRET), so no extra secrets are needed.
          </div>
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Site path</label>
              <input
                type="text"
                value={spSitePath}
                onChange={e => { setSpSitePath(e.target.value); setSpSaved(false); }}
                placeholder="altonvalley.sharepoint.com:/sites/Opportunities"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-[9px] text-gray-500 w-28 shrink-0">Folder path</label>
              <input
                type="text"
                value={spFolder}
                onChange={e => { setSpFolder(e.target.value); setSpSaved(false); }}
                placeholder="Shared Documents/Claude_CoWork"
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              />
              <button
                onClick={openSpBrowse}
                className="px-2.5 py-1.5 text-[9px] font-semibold border border-gray-300 rounded bg-white hover:bg-gray-50 whitespace-nowrap"
              >Browse…</button>
            </div>
          </div>
          <div className="flex justify-end mb-5">
            <button
              onClick={handleSpSave}
              disabled={spSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
            >
              {spSaving ? 'Saving…' : spSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>

          {/* Bid Tracker SharePoint — a separate site for bid folders */}
          <div className="mt-2 pt-5 border-t border-gray-100">
            <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
              <span>SharePoint — Pipeline Tracker</span>
              <span className="bg-blue-100 text-blue-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">Microsoft Graph</span>
            </div>
            <div className="text-[9px] text-gray-500 mb-2">
              When a bid is created, OPS creates a folder for it on this SharePoint site (a separate site from the SOW Opportunities site) and the bid detail page links straight to it. Uses the same app registration — no extra secrets.
            </div>
            <div className="text-[8px] text-gray-400 mb-3 bg-gray-50 border border-gray-200 rounded p-2">
              Site path uses the Graph format <code className="bg-gray-200 px-1 rounded">tenant.sharepoint.com:/sites/BidTracker</code>. Parent folder is relative to the document library root (blank = library root). Template folder is optional; its blank sub-folder structure is mirrored into each new bid folder.
            </div>
            <div className="flex flex-col gap-2 mb-3">
              <div className="flex gap-2 items-center">
                <label className="text-[9px] text-gray-500 w-28 shrink-0">Site path</label>
                <input type="text" value={btSite} onChange={e => { setBtSite(e.target.value); setBtSaved(false); }}
                  placeholder="altonvalley.sharepoint.com:/sites/BidTracker"
                  className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono" />
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-[9px] text-gray-500 w-28 shrink-0">Parent folder</label>
                <input type="text" value={btParent} onChange={e => { setBtParent(e.target.value); setBtSaved(false); }}
                  placeholder="Shared Documents (blank = library root)"
                  className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono" />
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-[9px] text-gray-500 w-28 shrink-0">Template folder</label>
                <input type="text" value={btTemplate} onChange={e => { setBtTemplate(e.target.value); setBtSaved(false); }}
                  placeholder="Optional, e.g. _Template Bid Folder"
                  className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono" />
              </div>
            </div>
            <div className="flex justify-end mb-5">
              <button onClick={handleBidSpSave} disabled={btSaving}
                className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap">
                {btSaving ? 'Saving…' : btSaved ? '✓ Saved' : 'Save'}
              </button>
            </div>
          </div>

          {/* Folder browser modal */}
          {spBrowseOpen && (
            <Modal title="Browse SharePoint Folders" onClose={() => setSpBrowseOpen(false)}>
                <div className="text-[9px] text-gray-500 mb-3 font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1">
                  /{spBrowsePath && spBrowsePath !== '/' ? spBrowsePath : ''}
                </div>
                {spBrowsePath && spBrowsePath !== '/' && (
                  <button
                    onClick={() => {
                      const parent = spBrowsePath.split('/').slice(0, -1).join('/');
                      spBrowse(parent);
                    }}
                    className="text-[9px] text-navy hover:underline mb-2 block"
                  >← Back</button>
                )}
                {spBrowseError && <div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-2">{spBrowseError}</div>}
                {spBrowseLoading && <div className="text-[9px] text-gray-400 py-4 text-center">Loading…</div>}
                {!spBrowseLoading && spBrowseFolders !== null && (
                  spBrowseFolders.length === 0
                    ? <div className="text-[9px] text-gray-400 py-4 text-center">No sub-folders here</div>
                    : <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded">
                        {spBrowseFolders.map(f => (
                          <div key={f.path} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                            <button
                              onClick={() => spBrowse(f.path)}
                              className="text-[10px] text-gray-700 hover:text-navy text-left flex items-center gap-1.5"
                            >
                              <span className="text-gray-400">📁</span> {f.name}
                            </button>
                            <button
                              onClick={() => { setSpFolder(f.path); setSpSaved(false); setSpBrowseOpen(false); }}
                              className="text-[8px] font-semibold text-navy hover:underline ml-2 shrink-0"
                            >Select</button>
                          </div>
                        ))}
                      </div>
                )}
                <div className="mt-4 flex justify-between items-center">
                  <button
                    onClick={() => { setSpFolder(spBrowsePath === '/' ? '' : spBrowsePath); setSpSaved(false); setSpBrowseOpen(false); }}
                    className="text-[9px] font-semibold text-navy hover:underline"
                  >Use current path</button>
                  <button onClick={() => setSpBrowseOpen(false)} className="px-3 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white">Close</button>
                </div>
            </Modal>
          )}

          {/* Style guide prompt */}
          <div className="border border-amber-200 bg-amber-50 rounded p-3">
            <div className="text-[10px] font-bold text-amber-800 mb-1">Generate a SOW style guide (run this yourself in Claude.ai)</div>
            <div className="text-[9px] text-amber-700 mb-2">
              Copy this prompt, open Claude.ai, attach your full SOW archive, and paste it in.
              Save the output as a <code className="bg-amber-100 px-1 rounded">.docx</code> into your CoWork folder
              alongside your 3 example SOWs — it will then be included automatically as context when OPS generates new SOWs.
              Run this whenever you want to refresh the guide after adding more SOWs.
            </div>
            <div className="relative">
              <pre className="text-[8.5px] text-gray-700 bg-white border border-gray-200 rounded p-2.5 whitespace-pre-wrap font-sans leading-relaxed select-all">{`Please read all of the SOW documents I've attached carefully. Once you have reviewed them all, produce a single concise reference guide (aim for 1,000–1,500 words) covering the following:

1. Structure & sections — what sections always appear, and in what order
2. Tone & language — how formal, what voice, any phrasing we use consistently
3. Milestone patterns — how work is typically phased, what milestones look like
4. Resource & role descriptions — how people and their responsibilities are described
5. Hardware & software — what we typically recommend or include, and how we present it
6. Commercial & pricing — how costs, day rates, and totals are structured and presented
7. What makes these SOWs effective — things to always include or emphasise
8. Things to avoid — any gaps, weaknesses, or patterns to steer away from

Format the output as a structured reference guide titled "Alton Valley SOW Style Guide". Do not include any preamble — start directly with the guide content. The guide will be saved back into our SharePoint reference folder and injected automatically when new SOWs are drafted.`}</pre>
              <button
                onClick={() => {
                  const prompt = `Please read all of the SOW documents I've attached carefully. Once you have reviewed them all, produce a single concise reference guide (aim for 1,000–1,500 words) covering the following:\n\n1. Structure & sections — what sections always appear, and in what order\n2. Tone & language — how formal, what voice, any phrasing we use consistently\n3. Milestone patterns — how work is typically phased, what milestones look like\n4. Resource & role descriptions — how people and their responsibilities are described\n5. Hardware & software — what we typically recommend or include, and how we present it\n6. Commercial & pricing — how costs, day rates, and totals are structured and presented\n7. What makes these SOWs effective — things to always include or emphasise\n8. Things to avoid — any gaps, weaknesses, or patterns to steer away from\n\nFormat the output as a structured reference guide titled "Alton Valley SOW Style Guide". Do not include any preamble — start directly with the guide content. The guide will be saved back into our SharePoint reference folder and injected automatically when new SOWs are drafted.`;
                  navigator.clipboard.writeText(prompt).then(() => {
                    setSpCopied(true);
                    setTimeout(() => setSpCopied(false), 2500);
                  });
                }}
                className="absolute top-2 right-2 px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-[8px] font-semibold rounded border border-amber-300 transition-colors"
              >
                {spCopied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        {/* TD SYNNEX */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1">TD SYNNEX Product Catalogue</div>
          <div className="text-[9px] text-gray-500 mb-3">
            API base URL for the TD SYNNEX Resellers API. Credentials (Client ID and Secret) are configured as Cloudflare secrets and cannot be changed here.
          </div>
          {tdsynnexStatus && (
            <div className={`mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded text-[8px] font-semibold border ${tdsynnexStatus.configured ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {tdsynnexStatus.configured ? '✓ Credentials configured' : '✗ Credentials not configured — set TDSYNNEX_CLIENT_ID and TDSYNNEX_CLIENT_SECRET as Cloudflare secrets'}
            </div>
          )}
          <div className="flex gap-2 items-center mb-3">
            <label className="text-[9px] text-gray-500 w-28 shrink-0">API Base URL</label>
            <input
              type="url"
              className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy font-mono"
              value={tdsynnexBaseUrl}
              onChange={e => { setTdsynnexBaseUrl(e.target.value); setTdsynnexUrlSaved(false); }}
              placeholder="https://api.tdsynnex.com/eu"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleTdsynnexUrlSave}
              disabled={tdsynnexUrlSaving}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50"
            >
              {tdsynnexUrlSaving ? 'Saving...' : tdsynnexUrlSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>

        {/* Weekly Hours Reminder Emails */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
            <span>Weekly Hours Reminder Emails</span>
            <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${timeReminderPaused ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {timeReminderPaused ? 'PAUSED' : 'ACTIVE'}
            </span>
          </div>
          <div className="text-[9px] text-gray-500 mb-2">
            Tuesday to Friday at 09:00 UTC, anyone with no time logged for the previous working day gets an automated nudge. Pause this while building/testing to avoid sending emails to real people. The Monday time chase (7am on the day a week closes, about the whole of last week) is separate and is not paused by this toggle.
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleTimeReminderToggle(!timeReminderPaused)}
              disabled={timeReminderSaving}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${!timeReminderPaused ? 'bg-emerald-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${!timeReminderPaused ? 'translate-x-4' : 'translate-x-1'}`} />
            </button>
            <span className="text-[10px] font-semibold text-gray-700">
              {timeReminderPaused ? 'Paused — no reminder emails will be sent' : 'Active — reminders fire Tuesday to Friday at 09:00 UTC'}
            </span>
            {timeReminderSaved && <span className="text-[9px] text-emerald-600 font-semibold">✓ Saved</span>}
          </div>
          <div className="mt-2 text-[8px] text-gray-400">
            Expected hours per person are configured on their People profile (default 37.5h/week). Only people logged below their target receive the email.
          </div>
        </div>

        {/* Default SOW Template */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="text-[10px] font-bold text-gray-700 mb-1">Default SOW Template</div>
          <div className="text-[9px] text-gray-500 mb-3">
            When generating a SOW and no type-specific Word template matches, OPS will fall back to this template to preserve your branding (header, footer, fonts, colours). Set it to your main branded <code className="bg-gray-100 px-1 rounded">.docx</code> template.
          </div>
          <div className="flex gap-2 items-center mb-3">
            <label className="text-[9px] text-gray-500 w-28 shrink-0">Template</label>
            {sowTemplates.length > 0 ? (
              <select
                value={defaultTplId}
                onChange={e => { setDefaultTplId(e.target.value); setDefaultTplSaved(false); }}
                className="flex-1 text-[10px] border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-navy"
              >
                <option value="">— None (build from scratch if no match) —</option>
                {sowTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}{t.sow_type ? ` (${t.sow_type})` : ''}</option>
                ))}
              </select>
            ) : (
              <div className="text-[9px] text-gray-400 italic">No active SOW templates found — upload one in Admin → SOW Templates first.</div>
            )}
          </div>
          {sowTemplates.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={handleDefaultTplSave}
                disabled={defaultTplSaving}
                className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50 whitespace-nowrap"
              >
                {defaultTplSaving ? 'Saving…' : defaultTplSaved ? '✓ Saved' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Standby Levels Manager ───────────────────────────────────────────────────
function StandbyLevelsManager() {
  const { toast } = useToast();
  const [levels,  setLevels]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, sort_order }
  const [adding,  setAdding]  = useState(false);
  const [newItem, setNewItem] = useState({ name: '', sort_order: '' });
  const [saving,  setSaving]  = useState(false);

  const load = async () => {
    setLoading(true);
    try { setLevels(await api.get('standby-levels?include_inactive=1')); }
    catch { setLevels([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addLevel = async () => {
    if (!newItem.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.post('standby-levels', {
        name: newItem.name.trim(),
        sort_order: parseInt(newItem.sort_order) || levels.length + 1,
      });
      setNewItem({ name: '', sort_order: '' });
      setAdding(false);
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const saveLevel = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`standby-levels/${editing.id}`, { name: editing.name, sort_order: parseInt(editing.sort_order) || 0 });
      setEditing(null);
      load();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  const toggleLevel = async (lvl) => {
    try {
      if (lvl.is_active) {
        await api.delete(`standby-levels/${lvl.id}`);
      } else {
        await api.put(`standby-levels/${lvl.id}`, { is_active: 1 });
      }
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold">Standby Levels</h3>
          <div className="text-[8px] text-gray-400">
            {levels.filter(l => l.is_active).length} active · offered on the PLOW's Standby row
          </div>
        </div>
        <button onClick={() => { setAdding(true); setEditing(null); }}
          className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">
          + Add Level
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Loading...</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead>
            <tr>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Level Name</th>
              <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Order</th>
              <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
              <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="bg-gold/5 border-b border-gold/20">
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px]" placeholder="e.g. Level 1" autoFocus
                    value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} />
                </td>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px] w-16 text-center" type="number" placeholder="1"
                    value={newItem.sort_order} onChange={e => setNewItem(p => ({ ...p, sort_order: e.target.value }))} />
                </td>
                <td />
                <td className="px-3 py-1.5 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={addLevel} disabled={saving}
                      className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                      {saving ? '...' : 'Save'}
                    </button>
                    <button onClick={() => setAdding(false)}
                      className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded hover:bg-gray-300">
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {levels.map(lvl => (
              <tr key={lvl.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!lvl.is_active ? 'opacity-40' : ''}`}>
                <td className="px-3 py-1.5">
                  {editing?.id === lvl.id
                    ? <input className="form-input text-[10px]" value={editing.name}
                        onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
                    : <span className="text-[10px] font-semibold">{lvl.name}</span>}
                </td>
                <td className="px-3 py-1.5 text-center">
                  {editing?.id === lvl.id
                    ? <input className="form-input text-[10px] w-16 text-center" type="number" value={editing.sort_order}
                        onChange={e => setEditing(p => ({ ...p, sort_order: e.target.value }))} />
                    : <span className="text-[10px] text-gray-400">{lvl.sort_order}</span>}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button onClick={() => toggleLevel(lvl)}
                    className={`w-8 h-4 rounded-full transition-colors ${lvl.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                    <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${lvl.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right">
                  {editing?.id === lvl.id ? (
                    <div className="flex gap-1 justify-end">
                      <button onClick={saveLevel} disabled={saving}
                        className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={() => setEditing(null)}
                        className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setEditing({ id: lvl.id, name: lvl.name, sort_order: lvl.sort_order })}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200">
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {levels.length === 0 && !loading && !adding && (
              <tr><td colSpan={4} className="text-center py-6 text-[10px] text-gray-400">
                No levels yet. Click "+ Add Level" to get started.
              </td></tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── Resource Types Manager ───────────────────────────────────────────────────
function ResourceTypesManager() {
  const { toast } = useToast();
  const [types, setTypes]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [adding, setAdding]     = useState(false);
  const [newType, setNewType]   = useState({ name: '', code: '', color: '#1B2A4A', default_band_code: '' });
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('resource-types', { include_inactive: '1' });
      setTypes(data);
    } catch { setTypes([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addType = async () => {
    if (!newType.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.post('resource-types', {
        name: newType.name.trim(),
        code: newType.code.trim() || newType.name.trim().toUpperCase().replace(/\s+/g,'_').slice(0,10),
        color: newType.color,
        default_band_code: newType.default_band_code.trim() || null,
      });
      setNewType({ name: '', code: '', color: '#1B2A4A', default_band_code: '' });
      setAdding(false);
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const saveType = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`resource-types/${editing.id}`, {
        name: editing.name, code: editing.code, color: editing.color, default_band_code: editing.default_band_code || null,
      });
      setEditing(null);
      load();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  const toggleType = async (rt) => {
    try {
      await api.put(`resource-types/${rt.id}`, { is_active: rt.is_active ? 0 : 1 });
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold">Resource Types</h3>
          <div className="text-[8px] text-gray-400">
            {types.filter(t => t.is_active).length} active · used in people capacity forecasting
          </div>
        </div>
        <button
          onClick={() => { setAdding(true); setEditing(null); }}
          className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80"
        >
          + Add Type
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Loading...</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead>
            <tr>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Colour</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Name</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Code</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Default Band</th>
              <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
              <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="bg-gold/5 border-b border-gold/20">
                <td className="px-3 py-1.5">
                  <div className="flex gap-1 flex-wrap">
                    {PRESET_COLORS.map(c => (
                      <button key={c} onClick={() => setNewType(p => ({ ...p, color: c }))}
                        className={`w-4 h-4 rounded-full border-2 ${newType.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                        style={{ background: c }} />
                    ))}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px]" placeholder="e.g. Contractor" value={newType.name}
                    onChange={e => setNewType(p => ({ ...p, name: e.target.value }))} autoFocus />
                </td>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px] w-24" placeholder="CONTR" value={newType.code}
                    onChange={e => setNewType(p => ({ ...p, code: e.target.value }))} />
                </td>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px] w-20" placeholder="B3" value={newType.default_band_code}
                    onChange={e => setNewType(p => ({ ...p, default_band_code: e.target.value }))} />
                </td>
                <td />
                <td className="px-3 py-1.5 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={addType} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                      {saving ? '...' : 'Save'}
                    </button>
                    <button onClick={() => setAdding(false)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded hover:bg-gray-300">
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {types.map(rt => (
              <tr key={rt.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!rt.is_active ? 'opacity-40' : ''}`}>
                <td className="px-3 py-1.5">
                  {editing?.id === rt.id ? (
                    <div className="flex gap-1 flex-wrap">
                      {PRESET_COLORS.map(c => (
                        <button key={c} onClick={() => setEditing(p => ({ ...p, color: c }))}
                          className={`w-4 h-4 rounded-full border-2 ${editing.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                          style={{ background: c }} />
                      ))}
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full" style={{ background: rt.color || '#1B2A4A' }} />
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {editing?.id === rt.id ? (
                    <input className="form-input text-[10px]" value={editing.name}
                      onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
                  ) : (
                    <span className="text-[10px] font-semibold" style={{ color: rt.color || '#1B2A4A' }}>{rt.name}</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {editing?.id === rt.id ? (
                    <input className="form-input text-[10px] w-24" value={editing.code || ''}
                      onChange={e => setEditing(p => ({ ...p, code: e.target.value }))} />
                  ) : (
                    <span className="text-[10px] text-gray-400 font-mono">{rt.code || '—'}</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {editing?.id === rt.id ? (
                    <input className="form-input text-[10px] w-20" placeholder="B3" value={editing.default_band_code || ''}
                      onChange={e => setEditing(p => ({ ...p, default_band_code: e.target.value }))} />
                  ) : (
                    <span className="text-[10px] text-gray-400 font-mono">{rt.default_band_code || '—'}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button onClick={() => toggleType(rt)}
                    className={`w-8 h-4 rounded-full transition-colors ${rt.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    title={rt.is_active ? 'Deactivate' : 'Activate'}>
                    <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${rt.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right">
                  {editing?.id === rt.id ? (
                    <div className="flex gap-1 justify-end">
                      <button onClick={saveType} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={() => setEditing(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditing({ id: rt.id, name: rt.name, code: rt.code || '', color: rt.color || '#1B2A4A', default_band_code: rt.default_band_code || '' })}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {types.length === 0 && !loading && !adding && (
              <tr><td colSpan={6} className="text-center py-6 text-[10px] text-gray-400">
                No resource types yet. Click "+ Add Type" to get started.
              </td></tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── Annual Leave & Company Holidays ─────────────────────────────────────────
//
// Two things that were asked for in El's retest of items 20 and 23:
//
//   "Our allowance is 23 days per year, not 25! So it should calculate from this."
//   "With an option in Admin to add 'Company Holidays' where we can block book a day out
//    for the whole company or team."
//
// The entitlement is one figure for the company, set here, because it was a number typed
// per person on the Edit Person form: 25 everywhere by default, 0 on at least one record,
// and shown next to a different figure on the Leave tab. FTE covers part-time, the
// long-service grant covers tenure, and a contractor accrues none, so one number and the
// rules cover everybody.
//
// A company holiday behaves exactly like a bank holiday: nobody books it, it comes off
// nobody's allowance, and a leave request spanning it does not count it.
function AnnualLeaveManager() {
  const { toast } = useToast();
  const [days, setDays] = useState('');
  const [savedDays, setSavedDays] = useState('');
  const [teams, setTeams] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [bank, setBank] = useState([]);
  const [form, setForm] = useState({ holiday_date: '', name: '', team_id: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, t, ch, bh] = await Promise.all([
      api.get('app-settings/annual_leave_days').catch(() => null),
      api.get('teams').catch(() => []),
      api.get('company-holidays').catch(() => []),
      api.get('bank-holidays').catch(() => []),
    ]);
    const v = d?.value != null && d.value !== '' ? String(d.value) : '23';
    setDays(v); setSavedDays(v);
    setTeams(Array.isArray(t) ? t : []);
    setHolidays(Array.isArray(ch) ? ch : []);
    setBank(Array.isArray(bh) ? bh : []);
  };
  useEffect(() => { load(); }, []);

  const saveDays = async () => {
    const n = parseFloat(days);
    if (!Number.isFinite(n) || n <= 0 || n > 50) { toast('Give the entitlement as a number of days.', 'error'); return; }
    setBusy(true);
    try {
      await api.put('app-settings/annual_leave_days', { value: String(n) });
      setSavedDays(String(n));
      toast('Saved. Everybody’s allowance is recalculated overnight, or immediately when their record is next saved.', 'success');
    } catch (e) { toast(e.message || 'Could not save that.', 'error'); }
    finally { setBusy(false); }
  };

  const addHoliday = async () => {
    setBusy(true);
    try {
      const r = await api.post('company-holidays', {
        holiday_date: form.holiday_date, name: form.name.trim(), team_id: form.team_id || null,
      });
      if (r?.error) throw new Error(r.error);
      setForm({ holiday_date: '', name: '', team_id: '' });
      await load();
      toast('Company holiday added. Nobody needs to book it.', 'success');
    } catch (e) { toast(e.message || 'Could not add that.', 'error'); }
    finally { setBusy(false); }
  };

  const removeHoliday = async (h) => {
    if (!window.confirm(`Remove ${h.holiday_date} — ${h.name}? People will have to book that day again.`)) return;
    setBusy(true);
    try { await api.delete(`company-holidays/${h.id}`); await load(); toast('Removed.', 'success'); }
    catch (e) { toast(e.message || 'Could not remove that.', 'error'); }
    finally { setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcomingBank = bank.filter(b => b.holiday_date >= today).slice(0, 12);

  return (
    <div className="lg:col-span-3 space-y-3">
      {/* ── The company's figure ── */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200">
          <h3 className="text-[11px] font-bold">Annual Leave Entitlement</h3>
          <div className="text-[8px] text-gray-400">One figure for the company — the system works out each person's from it</div>
        </div>
        <div className="p-3 space-y-2">
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Full-time days a year</label>
              <input type="number" step="0.5" min="1" max="50" value={days} onChange={e => setDays(e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-[11px] w-24" />
            </div>
            <button onClick={saveDays} disabled={busy || days === savedDays}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40">
              Save
            </button>
          </div>
          <p className="text-[9px] text-gray-500">
            Each person's allowance is this figure × their FTE, pro-rated from their start date in the year they join,
            plus one day for every completed year of service. A contractor accrues none. Nobody types a number on a
            person's record, so no two records can disagree about what the entitlement is.
          </p>
        </div>
      </div>

      {/* ── Company holidays ── */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200">
          <h3 className="text-[11px] font-bold">Company Holidays</h3>
          <div className="text-[8px] text-gray-400">
            {holidays.length} booked out · a day the company or one team is closed
          </div>
        </div>
        <div className="p-3 space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Date</label>
              <input type="date" value={form.holiday_date} onChange={e => setForm(f => ({ ...f, holiday_date: e.target.value }))}
                className="border border-gray-200 rounded px-2 py-1 text-[11px]" />
            </div>
            <div className="flex-1 min-w-[12rem]">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">What is it</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Christmas closure, team offsite…"
                className="border border-gray-200 rounded px-2 py-1 text-[11px] w-full" />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Who</label>
              <select value={form.team_id} onChange={e => setForm(f => ({ ...f, team_id: e.target.value }))}
                className="border border-gray-200 rounded px-2 py-1 text-[11px]">
                <option value="">Everybody</option>
                {[...teams].filter(t => t.is_active !== 0).sort((a, b) => a.name.localeCompare(b.name))
                  .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <button onClick={addHoliday} disabled={busy || !form.holiday_date || form.name.trim().length < 2}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40">
              Book it out
            </button>
          </div>
          <p className="text-[9px] text-gray-500">
            Nobody books a company holiday and it comes off nobody's allowance — it behaves exactly like a bank
            holiday. It shows in everyone's Leave &amp; Holidays, and a leave request spanning it does not count it as
            a day taken.
          </p>

          {holidays.length === 0 ? (
            <div className="text-center py-4 text-[9px] text-gray-400">No company holidays booked out.</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Date', 'What', 'Who', 'Added by', ''].map(h => (
                    <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holidays.map(h => (
                  <tr key={h.id} className={`border-b border-gray-100 ${h.holiday_date < today ? 'text-gray-400' : ''}`}>
                    <td className="px-2 py-1 text-[10px] font-semibold whitespace-nowrap">{h.holiday_date}</td>
                    <td className="px-2 py-1 text-[10px]">{h.name}</td>
                    <td className="px-2 py-1 text-[9px]">{h.team_name || 'Everybody'}</td>
                    <td className="px-2 py-1 text-[9px] text-gray-400">{h.created_by_name || '—'}</td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => removeHoliday(h)} disabled={busy}
                        className="text-[9px] text-red-500 hover:text-red-700 font-semibold">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {/* ── The bank holidays, for reference ── */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200">
          <h3 className="text-[11px] font-bold">Bank Holidays</h3>
          <div className="text-[8px] text-gray-400">England &amp; Wales · already off, already excluded from leave requests</div>
        </div>
        <div className="p-3 flex flex-wrap gap-1.5">
          {upcomingBank.map(b => (
            <span key={b.holiday_date} className="text-[9px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
              <strong>{b.holiday_date}</strong> {b.name}
            </span>
          ))}
          {!upcomingBank.length && <span className="text-[9px] text-gray-400">None loaded.</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Teams Manager ────────────────────────────────────────────────────────────
// Manages the actual `teams` table so that all dropdowns (tasks, people, forecast)
// stay in sync with whatever is set up here.
function TeamsManager() {
  const { toast } = useToast();
  const [teams, setTeams]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [editing, setEditing]   = useState(null); // { id, name, code, color }
  const [adding, setAdding]     = useState(false);
  const [newTeam, setNewTeam]   = useState({ name: '', code: '', color: '#A1723D' });
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('teams/all');
      setTeams(data);
    } catch { setTeams([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addTeam = async () => {
    if (!newTeam.name.trim()) { toast.error('Team name is required'); return; }
    setSaving(true);
    try {
      await api.post('teams', {
        name: newTeam.name.trim(),
        code: newTeam.code.trim() || newTeam.name.trim().toUpperCase().replace(/\s+/g, '_').slice(0, 10),
        color: newTeam.color,
      });
      setNewTeam({ name: '', code: '', color: '#A1723D' });
      setAdding(false);
      load();
    } catch (e) { toast.error('Failed to add team: ' + e.message); }
    setSaving(false);
  };

  const saveTeam = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`teams/${editing.id}`, { name: editing.name, code: editing.code, color: editing.color });
      setEditing(null);
      load();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  // Which colours are spoken for, and by whom. Used to fade the taken ones in the picker:
  // with twenty colours and fewer teams, a duplicate should now only ever be deliberate.
  const takenBy = new Map(
    (teams || []).filter(t => t.color).map(t => [String(t.color).toUpperCase(), t.name])
  );
  const takenColors = new Set(takenBy.keys());

  const toggleTeam = async (team) => {
    try {
      if (team.is_active) {
        await api.delete(`teams/${team.id}`);
      } else {
        await api.put(`teams/${team.id}`, { is_active: 1 });
      }
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold">Teams</h3>
          <div className="text-[8px] text-gray-400">
            {teams.filter(t => t.is_active).length} active · used in tasks, people profiles, resource forecast
          </div>
        </div>
        <button
          onClick={() => { setAdding(true); setEditing(null); }}
          className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80"
        >
          + Add Team
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Loading...</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead>
            <tr>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Colour</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Team Name</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Code</th>
              <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
              <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Add new row */}
            {adding && (
              <tr className="bg-gold/5 border-b border-gold/20">
                <td className="px-3 py-1.5">
                  <div className="flex gap-1 flex-wrap">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setNewTeam(p => ({ ...p, color: c }))}
                        // A colour another team already has is marked, not blocked: two
                        // teams sharing one makes the avatars useless for telling them
                        // apart, but there are legitimate reasons to do it on purpose.
                        title={takenColors.has(c.toUpperCase()) ? `Already used by ${takenBy.get(c.toUpperCase())}` : 'Free'}
                        className={`w-4 h-4 rounded-full border-2 ${newTeam.color === c ? 'border-gray-800 scale-110' : 'border-transparent'} ${takenColors.has(c.toUpperCase()) ? 'opacity-30' : ''}`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <div className="text-[7px] text-gray-400 mt-0.5">faded = already used by another team</div>
                </td>
                <td className="px-3 py-1.5">
                  <input
                    className="form-input text-[10px]"
                    placeholder="e.g. Infrastructure"
                    value={newTeam.name}
                    onChange={e => setNewTeam(p => ({ ...p, name: e.target.value }))}
                    autoFocus
                  />
                </td>
                <td className="px-3 py-1.5">
                  <input
                    className="form-input text-[10px] w-24"
                    placeholder="INFRA"
                    value={newTeam.code}
                    onChange={e => setNewTeam(p => ({ ...p, code: e.target.value }))}
                  />
                </td>
                <td />
                <td className="px-3 py-1.5 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={addTeam} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                      {saving ? '...' : 'Save'}
                    </button>
                    <button onClick={() => setAdding(false)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded hover:bg-gray-300">
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {teams.map(team => (
              <tr key={team.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!team.is_active ? 'opacity-40' : ''}`}>
                <td className="px-3 py-1.5">
                  {editing?.id === team.id ? (
                    <div className="flex gap-1 flex-wrap">
                      {PRESET_COLORS.map(c => {
                        const usedByAnother = takenBy.has(c.toUpperCase()) && takenBy.get(c.toUpperCase()) !== team.name;
                        return (
                          <button
                            key={c}
                            onClick={() => setEditing(p => ({ ...p, color: c }))}
                            title={usedByAnother ? `Already used by ${takenBy.get(c.toUpperCase())}` : 'Free'}
                            className={`w-4 h-4 rounded-full border-2 ${editing.color === c ? 'border-gray-800 scale-110' : 'border-transparent'} ${usedByAnother ? 'opacity-30' : ''}`}
                            style={{ background: c }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full" style={{ background: team.color || '#A1723D' }} />
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {editing?.id === team.id ? (
                    <input
                      className="form-input text-[10px]"
                      value={editing.name}
                      onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                    />
                  ) : (
                    <span className="text-[10px] font-semibold" style={{ color: team.color || '#1B2A4A' }}>{team.name}</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {editing?.id === team.id ? (
                    <input
                      className="form-input text-[10px] w-24"
                      value={editing.code || ''}
                      onChange={e => setEditing(p => ({ ...p, code: e.target.value }))}
                    />
                  ) : (
                    <span className="text-[10px] text-gray-400 font-mono">{team.code || '—'}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button
                    onClick={() => toggleTeam(team)}
                    className={`w-8 h-4 rounded-full transition-colors ${team.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    title={team.is_active ? 'Deactivate' : 'Activate'}
                  >
                    <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${team.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right">
                  {editing?.id === team.id ? (
                    <div className="flex gap-1 justify-end">
                      <button onClick={saveTeam} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={() => setEditing(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditing({ id: team.id, name: team.name, code: team.code || '', color: team.color || '#A1723D' })}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {teams.length === 0 && !loading && !adding && (
              <tr><td colSpan={5} className="text-center py-6 text-[10px] text-gray-400">
                No teams yet. Click "+ Add Team" to get started.
              </td></tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── SOW Issuance Manager (value approval bands + mailbox + mandatory gates) ──
function SowIssuanceManager() {
  const { toast } = useToast();
  const [mailbox,  setMailbox]  = useState('');
  const [expiryMailbox, setExpiryMailbox] = useState('');
  const [qaMand,   setQaMand]   = useState(true);
  const [apprMand, setApprMand] = useState(true);
  const [bands,    setBands]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [newBand,  setNewBand]  = useState({ label: '', min_value: '' });

  const load = async () => {
    setLoading(true);
    const [mb, expMb, qa, ap, bandRows] = await Promise.all([
      api.get('app-settings/sow_issuance_mailbox').catch(() => ({ value: '' })),
      api.get('app-settings/service_expiry_mailbox').catch(() => ({ value: '' })),
      api.get('app-settings/sow_qa_mandatory').catch(() => ({ value: '1' })),
      api.get('app-settings/sow_approval_mandatory').catch(() => ({ value: '1' })),
      api.get('sow-approval-bands').catch(() => []),
    ]);
    setMailbox(mb?.value || '');
    setExpiryMailbox(expMb?.value || '');
    setQaMand((qa?.value ?? '1') !== '0');
    setApprMand((ap?.value ?? '1') !== '0');
    setBands(bandRows || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveSetting = async (key, value) => {
    await api.put(`app-settings/${key}`, { value });
  };
  const saveMailbox = async () => {
    setSaving(true);
    try { await saveSetting('sow_issuance_mailbox', mailbox.trim()); toast.success('Mailbox saved.'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
    setSaving(false);
  };
  const saveExpiryMailbox = async () => {
    setSavingExpiry(true);
    try { await saveSetting('service_expiry_mailbox', expiryMailbox.trim()); toast.success('Mailbox saved.'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
    setSavingExpiry(false);
  };
  const toggleFlag = async (key, val, setter) => {
    setter(val);
    try { await saveSetting(key, val ? '1' : '0'); } catch (e) { toast.error(e.message || 'Save failed'); }
  };
  const updateBand = async (id, patch) => {
    setBands(bs => bs.map(b => b.id === id ? { ...b, ...patch } : b));
    try { await api.put(`sow-approval-bands/${id}`, patch); } catch (e) { toast.error(e.message || 'Save failed'); load(); }
  };
  const addBand = async () => {
    if (newBand.min_value === '') { toast.error('Enter a minimum value.'); return; }
    try {
      await api.post('sow-approval-bands', { label: newBand.label || null, min_value: parseFloat(newBand.min_value) });
      setNewBand({ label: '', min_value: '' });
      load();
    } catch (e) { toast.error(e.message || 'Add failed'); }
  };
  const removeBand = async (id) => {
    if (!confirm('Delete this approval band? Everybody ticked to approve it in Admin → Access loses that tick.')) return;
    try { await api.delete(`sow-approval-bands/${id}`); load(); } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const sorted = [...bands].sort((a, b) => (a.min_value || 0) - (b.min_value || 0));

  return (
    <div className="lg:col-span-3 space-y-3">
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200"><h3 className="text-[11px] font-bold">SOW Issuance Controls</h3>
          <div className="text-[8px] text-gray-400">Govern how Statements of Work are issued to clients.</div></div>
        <div className="p-3 space-y-3">
          {/* Mandatory gates */}
          <label className="flex items-center gap-2 text-[10px] text-gray-700">
            <input type="checkbox" className="accent-gold" checked={qaMand} onChange={e => toggleFlag('sow_qa_mandatory', e.target.checked, setQaMand)} />
            Require <strong>all QA review steps</strong> to be approved before a SOW can be issued
          </label>
          <label className="flex items-center gap-2 text-[10px] text-gray-700">
            <input type="checkbox" className="accent-gold" checked={apprMand} onChange={e => toggleFlag('sow_approval_mandatory', e.target.checked, setApprMand)} />
            Require <strong>value-based approval</strong> (below) before a SOW can be issued
          </label>
          {/* Mailbox */}
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW Issuance Mailbox (M365 shared mailbox)</label>
            <div className="flex gap-2">
              <input className="form-input text-[10px] flex-1" placeholder="sow@altonvalley.com" value={mailbox} onChange={e => setMailbox(e.target.value)} />
              <button onClick={saveMailbox} disabled={saving} className="px-3 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40">Save</button>
            </div>
            <div className="text-[8px] text-gray-400 mt-0.5">SOWs are sent from this mailbox via Microsoft 365 so a copy is retained in its Sent Items for audit. The app must hold Mail.Send permission for it.</div>
          </div>
        </div>
      </div>

      {/* Service expiry / renewals mailbox */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200"><h3 className="text-[11px] font-bold">Service Renewal Reminders</h3>
          <div className="text-[8px] text-gray-400">Where the 30-day-out reminder for expiring services (leased lines, support contracts, etc.) is sent.</div></div>
        <div className="p-3">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Service Expiry Mailbox</label>
          <div className="flex gap-2">
            <input className="form-input text-[10px] flex-1" placeholder="pmo@altonvalley.com" value={expiryMailbox} onChange={e => setExpiryMailbox(e.target.value)} />
            <button onClick={saveExpiryMailbox} disabled={savingExpiry} className="px-3 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40">Save</button>
          </div>
          <div className="text-[8px] text-gray-400 mt-0.5">Sent via Resend, like other automated notifications. Leave blank to disable the reminder.</div>
        </div>
      </div>

      {/* Approval bands */}
      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <div><h3 className="text-[11px] font-bold">Value Approval Bands</h3>
            <div className="text-[8px] text-gray-400">A SOW falls in the band with the greatest minimum value at or below its value (excl VAT).</div>
            <div className="text-[8px] text-gray-400">Who may approve the issue of a SOW in each band is set in Admin → Access, where every band has a row under Commercial. A new band starts with nobody ticked, so only an administrator can approve in it until somebody is.</div></div>
        </div>
        {loading ? <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Label</th>
              <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">From (£, excl VAT)</th>
              <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
              <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
            </tr></thead>
            <tbody>
              {sorted.map(b => (
                <tr key={b.id} className="border-b border-gray-50">
                  <td className="px-3 py-1.5"><input className="form-input text-[10px]" value={b.label || ''} onChange={e => setBands(bs => bs.map(x => x.id === b.id ? { ...x, label: e.target.value } : x))} onBlur={e => updateBand(b.id, { label: e.target.value })} /></td>
                  <td className="px-3 py-1.5"><input className="form-input text-[10px] w-28" type="number" value={b.min_value} onChange={e => setBands(bs => bs.map(x => x.id === b.id ? { ...x, min_value: e.target.value } : x))} onBlur={e => updateBand(b.id, { min_value: parseFloat(e.target.value) || 0 })} /></td>
                  <td className="px-3 py-1.5 text-center"><input type="checkbox" className="accent-gold" checked={!!b.is_active} onChange={e => updateBand(b.id, { is_active: e.target.checked ? 1 : 0 })} /></td>
                  <td className="px-3 py-1.5 text-right"><button onClick={() => removeBand(b.id)} className="text-[9px] text-red-500 hover:underline">Delete</button></td>
                </tr>
              ))}
              <tr className="bg-gold/5">
                <td className="px-3 py-1.5"><input className="form-input text-[10px]" placeholder="Band label" value={newBand.label} onChange={e => setNewBand(p => ({ ...p, label: e.target.value }))} /></td>
                <td className="px-3 py-1.5"><input className="form-input text-[10px] w-28" type="number" placeholder="0" value={newBand.min_value} onChange={e => setNewBand(p => ({ ...p, min_value: e.target.value }))} /></td>
                <td />
                <td className="px-3 py-1.5 text-right"><button onClick={addBand} className="px-2 py-0.5 bg-navy text-white text-[9px] font-semibold rounded">+ Add</button></td>
              </tr>
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ─── Role Definitions Manager (descriptions + permissions matrix) ─────────────
const ROLE_ORDER = ['admin','partner','commercial-lead','pmo','pm','bd','finance','billing','hr','engineer','contractor','client'];
const MODULE_LABELS = {
  'business-dev': 'Bids', 'commercial': 'Commercial', 'delivery': 'Delivery',
  'procurement': 'Procurement', 'hardware': 'Hardware', 'people': 'People',
  'finance': 'Finance', 'admin': 'Admin',
};

function RoleDefinitionsManager() {
  const { toast } = useToast();
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [view,    setView]    = useState('matrix'); // 'matrix' | 'descriptions'

  const load = () => {
    setLoading(true);
    api.get('role-definitions').then(rows => { setRoles(rows || []); setLoading(false); }).catch(() => { setRoles([]); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const saveDesc = async (role) => {
    try { await api.put(`role-definitions/${role.role_key}`, { description: role.description, label: role.label }); toast.success('Saved.'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
  };

  // Build ordered page list grouped by module
  const pageRows = [];
  for (const [modKey, pages] of Object.entries(MODULE_PAGES)) {
    for (const page of pages) {
      if (!pageRows.find(r => r.page === page)) {
        pageRows.push({ page, label: PAGE_LABELS[page] || page, module: modKey, moduleLabel: MODULE_LABELS[modKey] || modKey });
      }
    }
  }
  // Add any pages not in MODULE_PAGES
  for (const role of ROLE_ORDER) {
    for (const page of (PERMISSIONS[role] || [])) {
      if (!pageRows.find(r => r.page === page)) pageRows.push({ page, label: PAGE_LABELS[page] || page, module: 'other', moduleLabel: 'Other' });
    }
  }
  // Item 35: alphabetical within each module, keeping the modules in their own
  // order — this is a grid of every page against every role, and scanning it for
  // one page is impossible when the rows follow no order at all.
  {
    const moduleOrder = [...Object.keys(MODULE_PAGES), 'other'];
    pageRows.sort((a, b) =>
      moduleOrder.indexOf(a.module) - moduleOrder.indexOf(b.module)
      || String(a.label).localeCompare(String(b.label), 'en-GB', { sensitivity: 'base' }));
  }

  // Display roles in order (match what's in PERMISSIONS, use db label if available)
  const roleLabel = (key) => roles.find(r => r.role_key === key)?.label || key;
  const displayRoles = ROLE_ORDER.filter(k => PERMISSIONS[k]);

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-[11px] font-bold">Role Definitions & Permissions</h3>
          <div className="text-[8px] text-gray-400 mt-0.5">What each role can access. Descriptions are editable; page access is set in the codebase.</div>
          <div className="text-[8px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-1 mt-1 inline-block">🔒 Only admins can change roles, and no one can change their own. All role changes are audited.</div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => setView('matrix')} className={`px-2.5 py-1 text-[9px] font-semibold rounded ${view === 'matrix' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Permission Matrix</button>
          <button onClick={() => setView('descriptions')} className={`px-2.5 py-1 text-[9px] font-semibold rounded ${view === 'descriptions' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Descriptions</button>
        </div>
      </div>

      {loading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
        <>
          {view === 'matrix' && (
            <div className="overflow-auto max-h-[70vh]">
              <table className="text-[8px] w-full border-collapse" style={{ minWidth: 600 }}>
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th className="text-left px-2 py-1.5 border-b border-r border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase text-[7px] w-36">Page / Feature</th>
                    {displayRoles.map(key => (
                      <th key={key} className="px-1 py-1.5 border-b border-r border-gray-200 bg-gray-50 text-center font-bold text-navy whitespace-nowrap" style={{ minWidth: 54 }}>
                        <div>{roleLabel(key)}</div>
                        <div className="text-[6px] text-gray-400 font-normal font-mono">{key}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let lastMod = null;
                    return pageRows.map(({ page, label, module: mod, moduleLabel }) => {
                      const modHeader = mod !== lastMod;
                      lastMod = mod;
                      return [
                        modHeader && (
                          <tr key={`mod-${mod}`} className="bg-gold/5">
                            <td colSpan={displayRoles.length + 1} className="px-2 py-1 text-[7px] font-bold text-gold uppercase tracking-wide border-b border-gold/20">{moduleLabel}</td>
                          </tr>
                        ),
                        <tr key={page} className="hover:bg-gray-50 border-b border-gray-100">
                          <td className="px-2 py-1 text-gray-700 font-medium border-r border-gray-100 sticky left-0 bg-white">{label}</td>
                          {displayRoles.map(role => {
                            const has = (PERMISSIONS[role] || []).includes(page);
                            return (
                              <td key={role} className={`text-center border-r border-gray-100 ${has ? 'bg-emerald-50' : ''}`}>
                                {has ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-gray-200">·</span>}
                              </td>
                            );
                          })}
                        </tr>,
                      ];
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {view === 'descriptions' && (
            <div className="divide-y divide-gray-100">
              {roles.map(role => (
                <div key={role.role_key} className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-navy">{role.label}</span>
                    <span className="text-[8px] font-mono text-gray-400 bg-gray-100 px-1 rounded">{role.role_key}</span>
                    <span className="text-[8px] text-gray-400">{(PERMISSIONS[role.role_key] || []).length} pages</span>
                  </div>
                  <textarea
                    className="form-input text-[10px] w-full" rows={2}
                    value={role.description || ''}
                    onChange={e => setRoles(rs => rs.map(r => r.role_key === role.role_key ? { ...r, description: e.target.value } : r))}
                    onBlur={() => saveDesc(role)}
                    placeholder="Describe what this role is for…"
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Module Access Manager (menu visible, usage locked until enabled) ─────────
function ModuleAccessManager() {
  const { toast } = useToast();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('app-modules').then(rows => { setModules(rows || []); setLoading(false); }).catch(() => { setModules([]); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    setModules(ms => ms.map(m => m.id === id ? { ...m, ...patch } : m));
    try { await api.put(`app-modules/${id}`, patch); } catch (e) { toast.error(e.message || 'Save failed'); load(); }
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200"><h3 className="text-[11px] font-bold">Module Access</h3>
        <div className="text-[8px] text-gray-400">Switch a module on or off for the rollout. When locked, the menu still shows but opening a page displays a "coming soon" gate. Admins always bypass.</div></div>
      {loading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>
            <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Module</th>
            <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
            <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Locked message</th>
          </tr></thead>
          <tbody>
            {modules.map(m => (
              <tr key={m.id} className="border-b border-gray-50">
                <td className="px-3 py-1.5">
                  <div className="text-[10px] font-semibold text-gray-700">{m.label}</div>
                  <div className="text-[8px] text-gray-400">{(MODULE_PAGES[m.id] || []).length} pages</div>
                </td>
                <td className="px-3 py-1.5 text-center">
                  <button onClick={() => update(m.id, { is_enabled: m.is_enabled ? 0 : 1 })}
                    className={`px-2 py-0.5 text-[9px] font-bold rounded ${m.is_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                    {m.is_enabled ? 'Live' : 'Locked'}
                  </button>
                </td>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px]" placeholder="Optional message shown when locked"
                    value={m.locked_message || ''}
                    onChange={e => setModules(ms => ms.map(x => x.id === m.id ? { ...x, locked_message: e.target.value } : x))}
                    onBlur={e => update(m.id, { locked_message: e.target.value })} disabled={!!m.is_enabled} />
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── Contractor Onboarding Manager (configurable checklist) ───────────────────
const ONBOARDING_CATEGORIES = ['clearance', 'contract', 'compliance', 'it', 'admin'];
function ContractorOnboardingManager() {
  const { toast } = useToast();
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: 'compliance', depends_on: '', sort_order: '' });

  const load = () => {
    setLoading(true);
    api.get('contractor-onboarding-activities', { include_inactive: 1 })
      .then(r => { setActs(r || []); setLoading(false); })
      .catch(() => { setActs([]); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    setActs(a => a.map(x => x.id === id ? { ...x, ...patch } : x));
    try { await api.put(`contractor-onboarding-activities/${id}`, patch); } catch (e) { toast.error(e.message || 'Save failed'); load(); }
  };
  const add = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      await api.post('contractor-onboarding-activities', { ...form, depends_on: form.depends_on || null, sort_order: parseInt(form.sort_order) || 0 });
      setForm({ name: '', description: '', category: 'compliance', depends_on: '', sort_order: '' });
      setAdding(false); load();
    } catch (e) { toast.error(e.message || 'Add failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Deactivate this onboarding activity? Existing contractor progress is kept.')) return;
    try { await api.delete(`contractor-onboarding-activities/${id}`); load(); } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const activeActs = acts.filter(a => a.is_active);

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div><h3 className="text-[11px] font-bold">Contractor Onboarding Activities</h3>
          <div className="text-[8px] text-gray-400">The checklist every external contractor works through. Each contractor's progress is tracked on the Onboarding tab of their record on the Contractor Dashboard. SC depends on BPSS by default.</div></div>
        <button onClick={() => setAdding(v => !v)} className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">+ Add Activity</button>
      </div>
      {loading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>
            <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Activity</th>
            <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Category</th>
            <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Requires first</th>
            <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Order</th>
            <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
            <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
          </tr></thead>
          <tbody>
            {acts.map(a => (
              <tr key={a.id} className={`border-b border-gray-50 ${a.is_active ? '' : 'opacity-50'}`}>
                <td className="px-3 py-1.5">
                  <input className="form-input text-[10px]" value={a.name} onChange={e => setActs(x => x.map(y => y.id === a.id ? { ...y, name: e.target.value } : y))} onBlur={e => save(a.id, { name: e.target.value })} />
                  <input className="form-input text-[9px] mt-0.5 text-gray-500" placeholder="Description" value={a.description || ''} onChange={e => setActs(x => x.map(y => y.id === a.id ? { ...y, description: e.target.value } : y))} onBlur={e => save(a.id, { description: e.target.value })} />
                </td>
                <td className="px-3 py-1.5">
                  <select className="form-input text-[10px]" value={a.category || ''} onChange={e => save(a.id, { category: e.target.value })}>
                    <option value="">—</option>
                    {ONBOARDING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <select className="form-input text-[10px]" value={a.depends_on || ''} onChange={e => save(a.id, { depends_on: e.target.value || null })}>
                    <option value="">—</option>
                    {activeActs.filter(o => o.id !== a.id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5 text-center"><input className="form-input text-[10px] w-14 text-center" type="number" value={a.sort_order ?? 0} onChange={e => setActs(x => x.map(y => y.id === a.id ? { ...y, sort_order: e.target.value } : y))} onBlur={e => save(a.id, { sort_order: parseInt(e.target.value) || 0 })} /></td>
                <td className="px-3 py-1.5 text-center"><input type="checkbox" className="accent-gold" checked={!!a.is_active} onChange={e => save(a.id, { is_active: e.target.checked ? 1 : 0 })} /></td>
                <td className="px-3 py-1.5 text-right"><button onClick={() => remove(a.id)} className="text-[9px] text-red-500 hover:underline">Remove</button></td>
              </tr>
            ))}
            {adding && (
              <tr className="bg-gold/5">
                <td className="px-3 py-1.5"><input className="form-input text-[10px]" placeholder="Activity name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></td>
                <td className="px-3 py-1.5">
                  <select className="form-input text-[10px]" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                    {ONBOARDING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <select className="form-input text-[10px]" value={form.depends_on} onChange={e => setForm(p => ({ ...p, depends_on: e.target.value }))}>
                    <option value="">—</option>
                    {activeActs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-1.5 text-center"><input className="form-input text-[10px] w-14 text-center" type="number" placeholder="0" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} /></td>
                <td />
                <td className="px-3 py-1.5 text-right"><button onClick={add} className="px-2 py-0.5 bg-navy text-white text-[9px] font-semibold rounded">Save</button></td>
              </tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── QA Steps Manager ────────────────────────────────────────────────────────
const ROLES_FOR_QA = ['admin','hr','pm','pmo','finance','billing','engineer'];
function QaStepsManager() {
  const { toast } = useToast();
  const [steps,   setSteps]   = useState([]);
  const [teams,   setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(false);
  const [editing, setEditing] = useState(null); // step id being edited
  const [form,    setForm]    = useState({ name:'', description:'', required_role:'', required_team_id:'', sort_order:'', is_active:1 });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('qa-steps'),
      api.get('teams').catch(() => []),
    ]).then(([s, t]) => { setSteps(s || []); setTeams(t || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        required_role: form.required_role || null,
        required_team_id: form.required_team_id || null,
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (editing) {
        await api.put(`qa-steps/${editing}`, payload);
      } else {
        await api.post('qa-steps', payload);
      }
      setAdding(false); setEditing(null);
      setForm({ name:'', description:'', required_role:'', required_team_id:'', sort_order:'', is_active:1 });
      load();
    } catch(e) { toast.error('Save failed: ' + (e.data?.error || e.message)); }
  };

  const deactivate = async (id) => {
    if (!confirm('Deactivate this step? It will no longer appear in QA reviews.')) return;
    await api.delete(`qa-steps/${id}`);
    load();
  };

  const toggleActive = async (step) => {
    await api.put(`qa-steps/${step.id}`, { is_active: step.is_active ? 0 : 1 });
    load();
  };

  const startEdit = (step) => {
    setEditing(step.id);
    setAdding(false);
    setForm({ name: step.name, description: step.description || '', required_role: step.required_role || '', required_team_id: step.required_team_id || '', sort_order: step.sort_order ?? '', is_active: step.is_active });
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold">QA Review Steps</h3>
          <div className="text-[8px] text-gray-400">Steps that must be approved before a Draft SOW can be issued. Admins can always override.</div>
        </div>
        <button onClick={() => { setAdding(true); setEditing(null); setForm({ name:'', description:'', required_role:'', required_team_id:'', sort_order: steps.length * 10, is_active:1 }); }}
          className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">+ Add Step</button>
      </div>
      {loading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>
            {['#','Name','Requirement','Active',''].map(h => (
              <th key={h} className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {adding && (
              <tr className="border-b bg-blue-50/30">
                <td className="px-3 py-2"><input className="form-input w-14 text-[9px]" type="number" placeholder="10" value={form.sort_order} onChange={e => setForm(p=>({...p,sort_order:e.target.value}))} /></td>
                <td className="px-3 py-2 space-y-1">
                  <input className="form-input text-[9px] w-full" placeholder="Step name *" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} autoFocus />
                  <input className="form-input text-[9px] w-full" placeholder="Description (optional)" value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} />
                </td>
                <td className="px-3 py-2 space-y-1">
                  <select className="form-select text-[9px] w-full" value={form.required_role} onChange={e => setForm(p=>({...p,required_role:e.target.value}))}>
                    <option value="">— Any role —</option>
                    {ROLES_FOR_QA.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select className="form-select text-[9px] w-full" value={form.required_team_id} onChange={e => setForm(p=>({...p,required_team_id:e.target.value}))}>
                    <option value="">— Any team —</option>
                    {teams.filter(t=>t.is_active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </td>
                <td />
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={save} disabled={!form.name.trim()} className="px-2 py-1 bg-navy text-white text-[8px] font-semibold rounded disabled:opacity-40">Save</button>
                    <button onClick={() => setAdding(false)} className="px-2 py-1 border border-gray-200 text-[8px] rounded">Cancel</button>
                  </div>
                </td>
              </tr>
            )}
            {steps.map(step => editing === step.id ? (
              <tr key={step.id} className="border-b bg-blue-50/30">
                <td className="px-3 py-2"><input className="form-input w-14 text-[9px]" type="number" value={form.sort_order} onChange={e => setForm(p=>({...p,sort_order:e.target.value}))} /></td>
                <td className="px-3 py-2 space-y-1">
                  <input className="form-input text-[9px] w-full" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} autoFocus />
                  <input className="form-input text-[9px] w-full" placeholder="Description" value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} />
                </td>
                <td className="px-3 py-2 space-y-1">
                  <select className="form-select text-[9px] w-full" value={form.required_role} onChange={e => setForm(p=>({...p,required_role:e.target.value}))}>
                    <option value="">— Any role —</option>
                    {ROLES_FOR_QA.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select className="form-select text-[9px] w-full" value={form.required_team_id} onChange={e => setForm(p=>({...p,required_team_id:e.target.value}))}>
                    <option value="">— Any team —</option>
                    {teams.filter(t=>t.is_active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </td>
                <td />
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={save} disabled={!form.name.trim()} className="px-2 py-1 bg-navy text-white text-[8px] font-semibold rounded disabled:opacity-40">Save</button>
                    <button onClick={() => setEditing(null)} className="px-2 py-1 border border-gray-200 text-[8px] rounded">Cancel</button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={step.id} className={`border-b hover:bg-gray-50 ${!step.is_active ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-[9px] text-gray-400">{step.sort_order}</td>
                <td className="px-3 py-2">
                  <div className="text-[9px] font-semibold text-navy">{step.name}</div>
                  {step.description && <div className="text-[8px] text-gray-400">{step.description}</div>}
                </td>
                <td className="px-3 py-2">
                  {step.required_role && <span className="text-[7px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded mr-1">Role: {step.required_role}</span>}
                  {step.team_name && <span className="text-[7px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded">Team: {step.team_name}</span>}
                  {!step.required_role && !step.required_team_id && <span className="text-[8px] text-gray-400">Any team member</span>}
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleActive(step)} className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${step.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {step.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => startEdit(step)} className="text-[8px] text-gold hover:underline mr-2">Edit</button>
                </td>
              </tr>
            ))}
            {steps.length === 0 && !adding && (
              <tr><td colSpan="5" className="text-center py-6 text-[10px] text-gray-400">No QA steps defined yet.</td></tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// Admin-managed catalogue of supplier QA / compliance steps. Each step has a
// default applicability for new suppliers plus flags for whether it carries an
// expiry date and expects an uploaded document. Per-supplier requirement and
// status are managed on the supplier record (Procurement → Suppliers → QA).
function SupplierQaStepsManager() {
  const { toast } = useToast();
  const [steps, setSteps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = (order = '') => ({ name: '', description: '', default_required: 1, tracks_expiry: 1, requires_document: 1, sort_order: order });
  const [form, setForm] = useState(blank());

  const load = () => {
    setLoading(true);
    api.get('supplier-qa-steps').then(s => { setSteps(s || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        default_required: form.default_required ? 1 : 0,
        tracks_expiry: form.tracks_expiry ? 1 : 0,
        requires_document: form.requires_document ? 1 : 0,
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (editing) await api.put(`supplier-qa-steps/${editing}`, payload);
      else await api.post('supplier-qa-steps', payload);
      setAdding(false); setEditing(null); setForm(blank());
      load();
    } catch (e) { toast.error('Save failed: ' + (e.data?.error || e.message)); }
  };
  const toggleActive = async (step) => { await api.put(`supplier-qa-steps/${step.id}`, { is_active: step.is_active ? 0 : 1 }); load(); };
  const remove = async (step) => { if (!confirm('Deactivate this QA step? It will stop appearing for suppliers.')) return; await api.delete(`supplier-qa-steps/${step.id}`); load(); };
  const startEdit = (step) => { setEditing(step.id); setAdding(false); setForm({ name: step.name, description: step.description || '', default_required: step.default_required, tracks_expiry: step.tracks_expiry, requires_document: step.requires_document, sort_order: step.sort_order ?? '' }); };

  const editForm = (onCancel) => (
    <>
      <td className="px-3 py-2"><input className="form-input w-14 text-[9px]" type="number" placeholder="10" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} /></td>
      <td className="px-3 py-2 space-y-1">
        <input className="form-input text-[9px] w-full" placeholder="Step name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus />
        <input className="form-input text-[9px] w-full" placeholder="Description (optional)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1 text-[8px] font-semibold text-gray-600">
          <label className="flex items-center gap-1"><input type="checkbox" className="accent-navy" checked={!!form.default_required} onChange={e => setForm(p => ({ ...p, default_required: e.target.checked ? 1 : 0 }))} />Required by default</label>
          <label className="flex items-center gap-1"><input type="checkbox" className="accent-navy" checked={!!form.tracks_expiry} onChange={e => setForm(p => ({ ...p, tracks_expiry: e.target.checked ? 1 : 0 }))} />Tracks expiry</label>
          <label className="flex items-center gap-1"><input type="checkbox" className="accent-navy" checked={!!form.requires_document} onChange={e => setForm(p => ({ ...p, requires_document: e.target.checked ? 1 : 0 }))} />Needs document</label>
        </div>
      </td>
      <td />
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button onClick={save} disabled={!form.name.trim()} className="px-2 py-1 bg-navy text-white text-[8px] font-semibold rounded disabled:opacity-40">Save</button>
          <button onClick={onCancel} className="px-2 py-1 border border-gray-200 text-[8px] rounded">Cancel</button>
        </div>
      </td>
    </>
  );
  const Flag = ({ on, label }) => <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>{label}</span>;

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold">Supplier QA Steps</h3>
          <div className="text-[8px] text-gray-400">Compliance checks for suppliers. Per supplier you mark each required or not; required steps must be verified. Defaults below seed new suppliers.</div>
        </div>
        <button onClick={() => { setAdding(true); setEditing(null); setForm(blank(steps.length * 10)); }} className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80">+ Add Step</button>
      </div>
      {loading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{['#', 'Name', 'Defaults', 'Active', ''].map(h => (
            <th key={h} className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{h}</th>
          ))}</tr></thead>
          <tbody>
            {adding && <tr className="border-b bg-blue-50/30">{editForm(() => setAdding(false))}</tr>}
            {steps.map(step => editing === step.id ? (
              <tr key={step.id} className="border-b bg-blue-50/30">{editForm(() => setEditing(null))}</tr>
            ) : (
              <tr key={step.id} className={`border-b hover:bg-gray-50 ${!step.is_active ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-[9px] text-gray-400">{step.sort_order}</td>
                <td className="px-3 py-2">
                  <div className="text-[9px] font-semibold text-navy">{step.name}</div>
                  {step.description && <div className="text-[8px] text-gray-400">{step.description}</div>}
                </td>
                <td className="px-3 py-2"><div className="flex gap-1 flex-wrap">
                  <Flag on={step.default_required} label="Required" />
                  <Flag on={step.tracks_expiry} label="Expiry" />
                  <Flag on={step.requires_document} label="Doc" />
                </div></td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleActive(step)} className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${step.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{step.is_active ? 'Active' : 'Inactive'}</button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => startEdit(step)} className="text-[8px] text-gold hover:underline mr-2">Edit</button>
                  {step.is_active ? <button onClick={() => remove(step)} className="text-[8px] text-red-500 hover:underline">Remove</button> : null}
                </td>
              </tr>
            ))}
            {steps.length === 0 && !adding && (
              <tr><td colSpan="5" className="text-center py-6 text-[10px] text-gray-400">No supplier QA steps yet.</td></tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────
export default function AdminPage() {
  const { toast } = useToast();
  const [category, setCategory] = useState('sow_status');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, label, value, sort_order }
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ value: '', label: '', sort_order: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Thirty categories is more than fits on a screen, so the list is filtered
  // rather than scrolled. Matching on the key as well as the label means the
  // underlying name ('commercial_type') finds the screen it belongs to even
  // though the label reads differently ('SOW Commercial Type').
  const [catSearch, setCatSearch] = useState('');

  const load = async (cat) => {
    if (['team','standby_level','integrations','product_catalog_settings'].includes(cat)) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('config-options', { category: cat, include_inactive: '1' });
      setOptions(data);
    } catch (e) {
      setError('Failed to load options. Run migration 0002 first.');
      setOptions([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(category); }, [category]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`config-options/${editing.id}`, {
        label: editing.label,
        sort_order: parseInt(editing.sort_order) || 0,
      });
      setEditing(null);
      load(category);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const toggle = async (opt) => {
    try {
      if (opt.is_active) {
        await api.delete(`config-options/${opt.id}`);
      } else {
        await api.put(`config-options/${opt.id}`, { is_active: 1 });
      }
      load(category);
    } catch (e) {
      toast.error('Failed: ' + e.message);
    }
  };

  const addOption = async () => {
    if (!newItem.value.trim() || !newItem.label.trim()) {
      toast.error('Value and label are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('config-options', {
        category,
        value: newItem.value.trim(),
        label: newItem.label.trim(),
        sort_order: parseInt(newItem.sort_order) || options.length + 1,
      });
      setNewItem({ value: '', label: '', sort_order: '' });
      setAdding(false);
      load(category);
    } catch (e) {
      toast.error('Failed to add: ' + e.message);
    }
    setSaving(false);
  };

  const catLabel = CATEGORIES.find(c => c.key === category)?.label || category;

  const catQuery = catSearch.trim().toLowerCase();
  const visibleCategories = catQuery
    ? CATEGORIES.filter(c =>
        c.label.toLowerCase().includes(catQuery) ||
        c.key.replace(/_/g, ' ').includes(catQuery) ||
        c.key.includes(catQuery))
    : CATEGORIES;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Admin Settings</h2>
          <div className="text-[9px] text-gray-500">Manage dropdown options across the platform</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-3 text-[10px] text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Category list */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200">
            <h3 className="text-[10px] font-bold uppercase text-gray-500 tracking-wide">Dropdown Categories</h3>
          </div>
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <input
                type="search"
                value={catSearch}
                onChange={e => setCatSearch(e.target.value)}
                placeholder="Search settings…"
                aria-label="Search settings categories"
                className="w-full text-[10px] border border-gray-200 rounded pl-6 pr-2 py-1.5 bg-white focus:outline-none focus:border-navy"
              />
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] text-gray-400 pointer-events-none">⌕</span>
            </div>
          </div>
          {visibleCategories.length === 0 && (
            <div className="px-3 py-4 text-[9px] text-gray-400 text-center">
              No settings match “{catSearch.trim()}”.
            </div>
          )}
          {visibleCategories.map(cat => (
            <button
              key={cat.key}
              onClick={() => { setCategory(cat.key); setEditing(null); setAdding(false); }}
              className={`w-full text-left px-3 py-2 text-[10px] font-medium border-b border-gray-100 transition-colors ${
                category === cat.key
                  ? 'bg-gold/10 text-gold font-semibold border-l-2 border-l-gold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Special-case managers */}
        {category === 'team'             && <TeamsManager />}
        {category === 'resource_type'    && <ResourceTypesManager />}
        {category === 'standby_level'    && <StandbyLevelsManager />}
        {category === 'integrations'             && <IntegrationsManager />}
        {category === 'product_catalog_settings' && <ProductCatalogSettingsManager />}
        {category === 'qa_steps'                 && <QaStepsManager />}
        {category === 'supplier_qa_steps'        && <SupplierQaStepsManager />}
        {category === 'contractor_onboarding'    && <ContractorOnboardingManager />}
        {category === 'sow_issuance'             && <SowIssuanceManager />}
        {category === 'role_definitions'         && <RoleDefinitionsManager />}
        {category === 'roles_permissions'        && <AccessPage />}
        {category === 'module_access'            && <ModuleAccessManager />}
        {category === 'delivery_address'         && <DeliveryAddressPage />}
        {category === 'annual_leave'             && <AnnualLeaveManager />}

        {/* Config-options editor for all other categories */}
        {!['team','resource_type','standby_level','integrations','product_catalog_settings','qa_steps','supplier_qa_steps','contractor_onboarding','sow_issuance','role_definitions','roles_permissions','module_access','delivery_address','annual_leave'].includes(category) && (
          <div className="lg:col-span-3 bg-white border border-gray-200 rounded">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-[11px] font-bold">{catLabel}</h3>
                <div className="text-[8px] text-gray-400">{options.length} options</div>
              </div>
              <button
                onClick={() => { setAdding(true); setEditing(null); }}
                className="px-2.5 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80"
              >
                + Add Option
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-[10px] text-gray-400">Loading...</div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full">
                <thead>
                  <tr>
                    <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Label</th>
                    <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Value (stored)</th>
                    <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Order</th>
                    <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Active</th>
                    <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Add new row */}
                  {adding && (
                    <tr className="bg-gold/5 border-b border-gold/20">
                      <td className="px-3 py-1.5">
                        <input
                          className="form-input text-[10px]"
                          placeholder="Display label"
                          value={newItem.label}
                          onChange={e => setNewItem(p => ({ ...p, label: e.target.value }))}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className="form-input text-[10px]"
                          placeholder="Stored value"
                          value={newItem.value}
                          onChange={e => setNewItem(p => ({ ...p, value: e.target.value }))}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className="form-input text-[10px] w-16 text-center"
                          placeholder="0"
                          type="number"
                          value={newItem.sort_order}
                          onChange={e => setNewItem(p => ({ ...p, sort_order: e.target.value }))}
                        />
                      </td>
                      <td />
                      <td className="px-3 py-1.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={addOption} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                            {saving ? '...' : 'Save'}
                          </button>
                          <button onClick={() => setAdding(false)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded hover:bg-gray-300">
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {options.map(opt => (
                    <tr key={opt.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!opt.is_active ? 'opacity-40' : ''}`}>
                      <td className="px-3 py-1.5">
                        {editing?.id === opt.id ? (
                          <input
                            className="form-input text-[10px]"
                            value={editing.label}
                            onChange={e => setEditing(p => ({ ...p, label: e.target.value }))}
                          />
                        ) : (
                          <span className="text-[10px] font-medium">{opt.label}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="text-[10px] text-gray-400 font-mono">{opt.value}</span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {editing?.id === opt.id ? (
                          <input
                            className="form-input text-[10px] w-16 text-center"
                            type="number"
                            value={editing.sort_order}
                            onChange={e => setEditing(p => ({ ...p, sort_order: e.target.value }))}
                          />
                        ) : (
                          <span className="text-[10px] text-gray-400">{opt.sort_order}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <button
                          onClick={() => toggle(opt)}
                          className={`w-8 h-4 rounded-full transition-colors ${opt.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          title={opt.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${opt.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {editing?.id === opt.id ? (
                          <div className="flex gap-1 justify-end">
                            <button onClick={save} disabled={saving} className="px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-semibold rounded hover:bg-emerald-700">
                              {saving ? '...' : 'Save'}
                            </button>
                            <button onClick={() => setEditing(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold rounded">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditing({ id: opt.id, label: opt.label, value: opt.value, sort_order: opt.sort_order })}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}

                  {options.length === 0 && !loading && !adding && (
                    <tr><td colSpan={5} className="text-center py-6 text-[10px] text-gray-400">
                      No options yet. Click "+ Add Option" to get started.
                    </td></tr>
                  )}
                </tbody>
              </table></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCatalogSettingsManager() {
  const { toast } = useToast();
  const [commPct, setCommPct] = useState('');
  const [saved, setSaved]     = useState(false);
  const [saving, setSaving]   = useState(false);
  // The "N in stock now" lines under a picked product, and the stock check on
  // approving a hardware request (src/api/routes/productNotes.js). On unless '0'.
  const [stockLines, setStockLines]             = useState(true);
  const [stockLinesSaving, setStockLinesSaving] = useState(false);
  const [stockLinesSaved, setStockLinesSaved]   = useState(false);

  useEffect(() => {
    api.get('app-settings/product_catalog_default_commission_pct').then(r => {
      setCommPct(r?.value ?? '15');
    }).catch(() => setCommPct('15'));
    api.get('app-settings/hardware_stock_lines_enabled').then(r => setStockLines(r?.value !== '0')).catch(() => {});
  }, []);

  const handleStockLinesToggle = async (on) => {
    setStockLinesSaving(true); setStockLinesSaved(false);
    try {
      await api.put('app-settings/hardware_stock_lines_enabled', { value: on ? '1' : '0' });
      setStockLines(on);
      setStockLinesSaved(true);
      setTimeout(() => setStockLinesSaved(false), 3000);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setStockLinesSaving(false);
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put('app-settings/product_catalog_default_commission_pct', { value: String(parseFloat(commPct) || 15) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded p-4 space-y-4">
      <h3 className="text-[11px] font-bold text-navy">Product Catalog Settings</h3>
      <div className="max-w-sm space-y-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Default Commission %</label>
          <div className="text-[8px] text-gray-400 mb-2">Applied when no per-product commission override is set. Used to suggest a sell price based on the lowest supplier price.</div>
          <div className="flex items-center gap-2">
            <input
              className="form-input w-24"
              type="number" step="0.1" min="0" max="200"
              value={commPct}
              onChange={e => setCommPct(e.target.value)}
            />
            <span className="text-[9px] text-gray-500">%</span>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
            >{saving ? 'Saving…' : 'Save'}</button>
            {saved && <span className="text-[9px] text-emerald-600 font-semibold">Saved ✓</span>}
          </div>
        </div>
        <div className="p-2 bg-blue-50 border border-blue-100 rounded text-[8px] text-blue-700">
          Example: with 15% commission and a supplier price of £100, the suggested SOW sell price is £115.
        </div>
      </div>
      <div className="pt-4 border-t border-gray-100" data-testid="stock-lines-setting">
        <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
          <span>In Stock Lines on Hardware Requests</span>
          <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${stockLines ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
            {stockLines ? 'ON' : 'OFF'}
          </span>
        </div>
        <div className="text-[9px] text-gray-500 mb-2 max-w-2xl">
          Shows "N in stock now, not allocated" under a catalogue product picked on New Hardware Request, the request review and a PO's Add Line, and makes a reviewer tick that they checked with the inventory manager before approving a request while stock is showing. Supply notes are not affected.
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleStockLinesToggle(!stockLines)}
            disabled={stockLinesSaving}
            aria-pressed={stockLines}
            aria-label="In Stock lines on hardware requests"
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${stockLines ? 'bg-emerald-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${stockLines ? 'translate-x-4' : 'translate-x-1'}`} />
          </button>
          <span className="text-[10px] font-semibold text-gray-700">
            {stockLines ? 'On: stock is shown and approvals ask for the stock check' : 'Off: no stock lines, and no stock check on approval'}
          </span>
          {stockLinesSaved && <span className="text-[9px] text-emerald-600 font-semibold">Saved ✓</span>}
        </div>
      </div>
    </div>
  );
}
